import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    capitalize,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    IconButton,
    LinearProgress,
    Menu,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import { KeyboardArrowDown, KeyboardArrowLeft, KeyboardArrowRight, SwapVert, Close } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { getNeonColor, scrollable } from "../../theme";
import { targets, useReferentialContext } from "../../Context";
import { usePlayerLinkQueue } from "../../playerLinkQueue";
import { useThemeContext } from "../../themeContext";
import { RATING_WASH_ANCHORS, SCALE_STOPS, interpolateAnchored, paletteWinLoss, scaleColor as colorForScale, scaleGlowSx, shadeHex } from "../../settings/numberScale";
import { GameMetrics, Maps, Player, SoloQAccounts, SoloQGameSummaries, SoloQTeamRoundSummaries } from "../../reckon-api";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import formatters from "../../NeonComponents/Formatters";
import TeamIcon from "../../components/Icons/TeamIcon";
import AgentIcon from "../../components/Icons/AgentIcon";
import FadedAgentArt, { AGENT_BG_INSET, agentArtSize } from "../../components/Icons/FadedAgentArt";
import { agentAccent } from "../../components/Icons/agentColors";
import RoleIcon from "../../components/Icons/RoleIcon";
import RoundEndIcon from "../../components/Icons/RoundEndIcon";
import RankIcon, { fullRankFromNumber, rankLabel, rankPalette, useCompetitiveTier } from "../../components/Icons/RankIcon";
import WindowContainer from "../../components/layout/WindowContainer";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import PlayerSelect from "../../components/selectors/PlayerSelect";
import { getMapAsset } from "../../valorant-api/mapAssets";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BreakdownAgg {
    id: string;
    games: number;
    winRate: number;
    roundWinRate: number | null;
    atkWinRate: number | null;
    defWinRate: number | null;
    kills: number;
    deaths: number;
    acs: number;
    adr: number;
    rating: number;
    hs: number;
    firstKills: number;
    firstDeaths: number;
}

interface SideWinMetrics {
    roundWin?: GameMetrics;
    atkWin?: GameMetrics;
    defWin?: GameMetrics;
    pistolWin?: GameMetrics;
}

interface BreakdownFilter {
    kind: "agent" | "role" | "account" | "map";
    id: string;
}

interface MapRateStat {
    rate: number | null;
    wins: number | null;
    losses: number | null;
}

interface MapStatsRow {
    map: string;
    games: number;
    wins: number;
    losses: number;
    winRate: number;
    round: MapRateStat;
    pistol: MapRateStat;
    atk: MapRateStat;
    def: MapRateStat;
}

interface AccountRow {
    account: SoloQAccounts;
    games: number;
    wins: number;
    rankTier: number | null;
    lastIgName: string;
    mainAgent: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hsUnit(value: number) {
    return value > 1 ? value / 100 : value;
}

function teamLabel(teamId?: string | null) {
    return teamId?.split("_")[0] || "";
}

function mapArtUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.splash) return mapRef.splash;
    if (mapRef?.uuid) return `https://media.valorant-api.com/maps/${mapRef.uuid}/splash.png`;
    return getMapAsset(mapId, "splash").replace("/upload/", "/upload/c_fill,h_360,w_1440,q_auto/");
}

function mapBannerUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.list_view_icon) return mapRef.list_view_icon;
    if (mapRef?.uuid) return `https://media.valorant-api.com/maps/${mapRef.uuid}/listviewicon.png`;
    return getMapAsset(mapId, "listViewIcon").replace("/upload/", "/upload/c_fill,h_72,w_200/");
}

function formatRoleName(role?: string | null) {
    if (!role) return "";
    const name = role.includes("::") ? role.split("::").pop() || role : role;
    return name.replace(/_/g, " ");
}

function metricBlob(metric: GameMetrics) {
    return `${metric.code} ${metric.label ?? ""} ${metric._short ?? ""}`.toLowerCase();
}

function metricForSoloq(metric: GameMetrics) {
    return !metric.available_for || metric.available_for.toLowerCase().includes(targets.soloq);
}

function findMetric(metrics: GameMetrics[], exactCodes: string[], pattern: RegExp) {
    const codes = new Set(exactCodes);
    return (
        metrics.find((metric) => metricForSoloq(metric) && codes.has(metric.code.toLowerCase())) ||
        metrics.find((metric) => metricForSoloq(metric) && pattern.test(metricBlob(metric)))
    );
}

function findSideWinMetrics(metrics: GameMetrics[]): SideWinMetrics {
    return {
        roundWin: findMetric(metrics, ["round_winrate", "rwr", "round_wr", "rwin", "round_win", "rwinrate"], /round.*win|rwin|\brwr\b|rwr%/),
        atkWin: findMetric(
            metrics,
            ["atk_winrate", "attack_winrate", "atk_wr", "atk_win", "winrate_atk", "attack_wr", "atk_rwr", "attack_rwr", "rwr_atk"],
            /(atk|attack).*(win|wr)|win.*(atk|attack)/,
        ),
        defWin: findMetric(
            metrics,
            ["def_winrate", "defense_winrate", "defence_winrate", "def_wr", "def_win", "winrate_def", "defense_wr", "def_rwr", "defense_rwr", "rwr_def"],
            /(def|defense|defence).*(win|wr)|win.*(def|defense|defence)/,
        ),
        pistolWin: findMetric(
            metrics,
            ["pistol_winrate", "pistol_wr", "pistol_win", "winrate_pistol", "pistol_rwr", "rwr_pistol", "pwr"],
            /pistol.*(win|wr)|win.*pistol|\bpwr\b/,
        ),
    };
}

function agentFullPortraitUrl(
    agentName?: string | null,
    agents?: { id: string; uuid?: string; full_portrait?: string | null }[],
) {
    if (!agentName || !agents?.length) return undefined;
    const ref = agents.find((a) => a.id.toLowerCase() === agentName.toLowerCase());
    if (ref?.full_portrait) return ref.full_portrait;
    if (ref?.uuid) return `https://media.valorant-api.com/agents/${ref.uuid}/fullportrait.png`;
    return undefined;
}

function AgentRoleMark({
    agent,
    role: roleOverride,
    roleIcon: roleIconOverride,
}: {
    agent?: string | null;
    role?: string | null;
    roleIcon?: string | null;
}) {
    const { agentsReferential } = useReferentialContext();
    const agentRef = agent
        ? agentsReferential.find((a) => a.id.toLowerCase() === agent.toLowerCase())
        : undefined;
    const role = formatRoleName(roleOverride) || formatRoleName(agentRef?.role);
    const icon = roleIconOverride || agentRef?.role_icon;
    const slotSx = {
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        position: "relative" as const,
        zIndex: 2,
    };
    if (!role) return <Box sx={slotSx} />;

    return (
        <Stack spacing={0.15} sx={slotSx}>
            <RoleIcon role={role} icon={icon} size={18} />
            <Typography
                sx={{
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 1px 6px rgba(0,0,0,0.7)",
                    whiteSpace: "nowrap",
                    lineHeight: 1.1,
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                }}
            >
                {role}
            </Typography>
        </Stack>
    );
}

const BREAKDOWN_AGGREGATES = [
    "win__avg",
    "pk__cnt",
    "acs__avg",
    "adr__avg",
    "kast__avg",
    "rating_vlr__avg",
    "headshot_percent__avg",
    "kills__avg",
    "deaths__avg",
    "first_kills__sum",
    "first_deaths__sum",
    "first_kills__avg",
    "first_deaths__avg",
    "rounds_won__sum",
    "rounds_lost__sum",
    "rounds_played__sum",
];

const SUMMARY_LIST_FIELDS = [
    "pk,date,map,agent,win,puuid,ig_name,rank_tier,kills,deaths,assists,acs,adr,kast,rating_vlr,headshot_percent,first_kills,first_deaths,rounds_won,rounds_lost,rounds_played,game_id,team,competitive_player",
];
const SUMMARY_JOIN_FIELDS = ["agent__role", "agent__role_icon"];

function numOrNull(row: any, ...keys: string[]): number | null {
    for (const key of keys) {
        if (!key) continue;
        const value = row?.[key];
        if (value != null && value !== "" && !Number.isNaN(Number(value))) return Number(value);
    }
    return null;
}

function num(row: any, ...keys: string[]): number {
    return numOrNull(row, ...keys) ?? 0;
}

function ratio(wins: number | null, losses: number | null, played: number | null): number | null {
    const denom = played && played > 0 ? played : wins != null && losses != null ? wins + losses : 0;
    if (!denom || wins == null) return null;
    return wins / denom;
}

function asAggregateRows(result: any, groupField: string): any[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    if (result && typeof result === "object") {
        return Object.entries(result).map(([key, value]) => {
            if (value && typeof value === "object") {
                return { [groupField]: (value as any)[groupField] ?? key, ...(value as object) };
            }
            return { [groupField]: key };
        });
    }
    return [];
}

function groupValue(row: any, groupField: string) {
    const leaf = groupField.includes("__") ? groupField.split("__").pop() : groupField;
    const raw = row?.[groupField] ?? (leaf ? row?.[leaf] : undefined) ?? row?.pk ?? row?.id;
    if (raw != null && raw !== "" && typeof raw !== "object") return String(raw);
    if (raw && typeof raw === "object") {
        const nested = raw.id ?? raw.role ?? raw.name ?? raw.pk;
        if (nested != null && nested !== "") return String(nested);
    }
    return "";
}

function fromAggregateRow(
    row: any,
    groupField: string,
    formatId: (value: string) => string = (value) => value,
    sideMetrics: SideWinMetrics = {},
): BreakdownAgg | null {
    const id = formatId(groupValue(row, groupField));
    if (!id) return null;
    const games = num(row, "games", "pk__cnt", "pk__count", "id__cnt", "game_id__cnt");
    return {
        id,
        games,
        winRate: num(row, "win__avg", "win"),
        roundWinRate:
            numOrNull(row, sideMetrics.roundWin?.code || "") ??
            ratio(
                numOrNull(row, "rounds_won__sum", "rounds_won"),
                numOrNull(row, "rounds_lost__sum", "rounds_lost"),
                numOrNull(row, "rounds_played__sum", "rounds_played"),
            ),
        atkWinRate: numOrNull(row, sideMetrics.atkWin?.code || ""),
        defWinRate: numOrNull(row, sideMetrics.defWin?.code || ""),
        kills: num(row, "kills__avg", "kills_total", "kills"),
        deaths: num(row, "deaths__avg", "deaths_total", "deaths"),
        acs: num(row, "acs__avg", "acs"),
        adr: num(row, "adr__avg", "adr"),
        rating: num(row, "rating_vlr__avg", "rating_vlr", "rating"),
        hs: num(row, "headshot_percent__avg", "headshot_percent"),
        firstKills:
            num(row, "first_kills__sum", "first_blood_total") ||
            num(row, "first_kills__avg", "first_kills") * (games || 1),
        firstDeaths:
            num(row, "first_deaths__sum", "first_deaths_total") ||
            num(row, "first_deaths__avg", "first_deaths") * (games || 1),
    };
}

function average(sum: number, n: number) {
    return n ? sum / n : 0;
}

function aggregateFromSummaries(
    summaries: SoloQGameSummaries[],
    keyOf: (summary: SoloQGameSummaries) => string,
): BreakdownAgg[] {
    const buckets = new Map<
        string,
        {
            games: number;
            wins: number;
            kills: number;
            deaths: number;
            acs: number;
            adr: number;
            rating: number;
            hs: number;
            firstKills: number;
            firstDeaths: number;
            roundWins: number;
            roundLosses: number;
            roundPlayed: number;
        }
    >();
    for (const summary of summaries) {
        const id = keyOf(summary);
        if (!id) continue;
        const cur = buckets.get(id) || {
            games: 0,
            wins: 0,
            kills: 0,
            deaths: 0,
            acs: 0,
            adr: 0,
            rating: 0,
            hs: 0,
            firstKills: 0,
            firstDeaths: 0,
            roundWins: 0,
            roundLosses: 0,
            roundPlayed: 0,
        };
        cur.games += 1;
        if (summary.win) cur.wins += 1;
        cur.kills += summary.kills || 0;
        cur.deaths += summary.deaths || 0;
        cur.acs += summary.acs || 0;
        cur.adr += summary.adr || 0;
        cur.rating += summary.rating_vlr || 0;
        cur.hs += summary.headshot_percent || 0;
        cur.firstKills += summary.first_kills || 0;
        cur.firstDeaths += summary.first_deaths || 0;
        cur.roundWins += summary.rounds_won || 0;
        cur.roundLosses += summary.rounds_lost || 0;
        cur.roundPlayed += summary.rounds_played || 0;
        buckets.set(id, cur);
    }
    return [...buckets.entries()]
        .map(([id, b]) => ({
            id,
            games: b.games,
            winRate: average(b.wins, b.games),
            roundWinRate: ratio(b.roundWins, b.roundLosses, b.roundPlayed),
            atkWinRate: null,
            defWinRate: null,
            kills: average(b.kills, b.games),
            deaths: average(b.deaths, b.games),
            acs: average(b.acs, b.games),
            adr: average(b.adr, b.games),
            rating: average(b.rating, b.games),
            hs: average(b.hs, b.games),
            firstKills: b.firstKills,
            firstDeaths: b.firstDeaths,
        }))
        .sort((a, b) => b.games - a.games || b.rating - a.rating);
}

function roleFromSummary(
    summary: SoloQGameSummaries,
    agents: { id: string; role?: string | null }[],
): string {
    const annotated = summary as SoloQGameSummaries & { agent__role?: string; agent_role?: string };
    const annotatedRole = annotated.agent__role || annotated.agent_role;
    if (annotatedRole) return formatRoleName(annotatedRole);
    const agent = agents.find((a) => a.id.toLowerCase() === (summary.agent || "").toLowerCase());
    return formatRoleName(agent?.role);
}

function roleForAgent(agentId: string, agents: { id: string; role?: string | null }[]) {
    const agent = agents.find((a) => a.id.toLowerCase() === (agentId || "").toLowerCase());
    return formatRoleName(agent?.role);
}

function accountKey(account: { puuid?: string | null; id?: number | string | null }) {
    return account.puuid || (account.id != null ? String(account.id) : "");
}

function matchesBreakdownFilter(
    summary: SoloQGameSummaries,
    filter: BreakdownFilter | null,
    agents: { id: string; role?: string | null }[],
) {
    if (!filter) return true;
    if (filter.kind === "account") return (summary.puuid || "").toLowerCase() === filter.id.toLowerCase();
    if (filter.kind === "agent") return (summary.agent || "").toLowerCase() === filter.id.toLowerCase();
    if (filter.kind === "map") return (summary.map || "").toLowerCase() === filter.id.toLowerCase();
    return roleFromSummary(summary, agents).toLowerCase() === filter.id.toLowerCase();
}

const EMPTY_MAP_RATE: MapRateStat = { rate: null, wins: null, losses: null };

function rateFromRecord(wins: number, losses: number): MapRateStat {
    const total = wins + losses;
    if (total <= 0) return EMPTY_MAP_RATE;
    return { rate: wins / total, wins, losses };
}

function rateFromAvg(rate: number | null): MapRateStat {
    if (rate == null) return EMPTY_MAP_RATE;
    return { rate, wins: null, losses: null };
}

function mapRateStatsFromMetric(rows: any[], rateKey: string, countKeys: string[] = []): Map<string, MapRateStat> {
    const out = new Map<string, MapRateStat>();
    if (!rateKey) return out;
    for (const row of rows) {
        const map = row?.map ?? row?.id;
        const rateRaw = row?.[rateKey] ?? row?.[`${rateKey}__avg`] ?? (rateKey === "win__avg" ? row?.win : undefined);
        if (!map || rateRaw === null || rateRaw === undefined || Number.isNaN(Number(rateRaw))) continue;
        let rate = Number(rateRaw);
        if (rate > 1) rate = rate / 100;
        const rounds = countKeys.reduce((n, key) => n || Number(row?.[key]) || 0, 0);
        const wins = rounds > 0 ? Math.round(rate * rounds) : null;
        const losses = wins === null ? null : Math.max(0, rounds - wins);
        out.set(String(map).toLowerCase(), { rate, wins, losses });
    }
    return out;
}

function mergeMapRateMaps(primary: Map<string, MapRateStat>, fallback: Map<string, MapRateStat>) {
    const out = new Map(primary);
    for (const [key, value] of fallback) {
        if (!out.has(key)) out.set(key, value);
    }
    return out;
}

function mergeMapRoundRates(
    base: MapStatsRow[],
    pistol: Map<string, MapRateStat>,
    atk: Map<string, MapRateStat>,
    def: Map<string, MapRateStat>,
): MapStatsRow[] {
    return base.map((row) => {
        const key = row.map.toLowerCase();
        return {
            ...row,
            pistol: pistol.get(key) ?? row.pistol,
            atk: atk.get(key) ?? row.atk,
            def: def.get(key) ?? row.def,
        };
    });
}

const EMPTY_MAP_ROUND_RATES = {
    pistol: new Map<string, MapRateStat>(),
    atk: new Map<string, MapRateStat>(),
    def: new Map<string, MapRateStat>(),
};

function buildMapStats(summaries: SoloQGameSummaries[], breakdown: BreakdownAgg[]): MapStatsRow[] {
    const byMap = new Map<string, { id: string; games: number; wins: number; roundWins: number; roundLosses: number }>();
    for (const summary of summaries) {
        const id = (summary.map || "").trim();
        if (!id) continue;
        const key = id.toLowerCase();
        const cur = byMap.get(key) || { id, games: 0, wins: 0, roundWins: 0, roundLosses: 0 };
        cur.games += 1;
        if (summary.win) cur.wins += 1;
        cur.roundWins += summary.rounds_won ?? 0;
        cur.roundLosses += summary.rounds_lost ?? 0;
        byMap.set(key, cur);
    }
    const extra = new Map(breakdown.map((row) => [row.id.toLowerCase(), row]));
    return [...byMap.values()]
        .map((stats) => {
            const row = extra.get(stats.id.toLowerCase());
            return {
                map: stats.id,
                games: stats.games,
                wins: stats.wins,
                losses: stats.games - stats.wins,
                winRate: stats.games ? stats.wins / stats.games : 0,
                round: rateFromRecord(stats.roundWins, stats.roundLosses),
                pistol: EMPTY_MAP_RATE,
                atk: rateFromAvg(row?.atkWinRate ?? null),
                def: rateFromAvg(row?.defWinRate ?? null),
            };
        })
        .sort((a, b) => b.games - a.games || b.winRate - a.winRate);
}

function buildAccountRows(accounts: SoloQAccounts[], summaries: SoloQGameSummaries[]): AccountRow[] {
    const byPuuid = new Map<
        string,
        {
            games: number;
            wins: number;
            lastIgName: string;
            lastDate: number;
            agents: Map<string, { id: string; count: number; lastDate: number }>;
        }
    >();
    for (const s of summaries) {
        const cur = byPuuid.get(s.puuid) || {
            games: 0,
            wins: 0,
            lastIgName: "",
            lastDate: 0,
            agents: new Map(),
        };
        cur.games += 1;
        if (s.win) cur.wins += 1;
        const ts = dayjs(s.date).valueOf();
        if (ts >= cur.lastDate) {
            cur.lastDate = ts;
            cur.lastIgName = s.ig_name || cur.lastIgName;
        }
        const agent = (s.agent || "").trim();
        if (agent) {
            const key = agent.toLowerCase();
            const prev = cur.agents.get(key) || { id: agent, count: 0, lastDate: 0 };
            prev.count += 1;
            if (ts >= prev.lastDate) prev.lastDate = ts;
            cur.agents.set(key, prev);
        }
        byPuuid.set(s.puuid, cur);
    }

    const mainAgentOf = (stats?: { agents: Map<string, { id: string; count: number; lastDate: number }> }) => {
        if (!stats?.agents.size) return "";
        let best: { id: string; count: number; lastDate: number } | null = null;
        for (const agent of stats.agents.values()) {
            if (!best || agent.count > best.count || (agent.count === best.count && agent.lastDate > best.lastDate)) {
                best = agent;
            }
        }
        return best?.id || "";
    };

    return [...accounts]
        .map((account) => {
            const stats = byPuuid.get(account.puuid);
            return {
                account,
                games: stats?.games ?? 0,
                wins: stats?.wins ?? 0,
                rankTier: account.rank_tier ?? null,
                lastIgName: stats?.lastIgName || account.account_name,
                mainAgent: mainAgentOf(stats),
            };
        })
        .sort((a, b) => (b.rankTier ?? -1) - (a.rankTier ?? -1) || b.games - a.games);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function useScale() {
    const { numberScale } = useThemeContext();
    const { win, loss } = paletteWinLoss(numberScale);
    return {
        color: (value: number | null, stops: number[]) => colorForScale(value, stops, numberScale),
        glow: (color: string) => scaleGlowSx(color, numberScale.glow),
        glowAmount: numberScale.glow,
        win,
        loss,
        winWash: shadeHex(win, 0.12),
        lossWash: shadeHex(loss, 0.12),
        ratingColor: (rating: number | null) =>
            interpolateAnchored(rating, RATING_WASH_ANCHORS, numberScale.colors, numberScale.colors[2]),
    };
}

function RateCell({
    rate,
    bar = true,
}: {
    rate: number | null;
    neonColor?: string;
    bar?: boolean;
}) {
    const scale = useScale();
    if (rate == null) {
        return (
            <Typography sx={{ color: "rgba(255,255,255,0.35)", fontWeight: 600, fontSize: "0.78rem" }}>—</Typography>
        );
    }
    const color = scale.color(rate, SCALE_STOPS.rate);
    return (
        <>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    ...scale.glow(color),
                }}
            >
                {formatters.percent(rate)}
            </Typography>
            {bar ? (
                <LinearProgress
                    variant="determinate"
                    value={Math.max(rate * 100, 4)}
                    sx={{
                        mt: 0.35,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: "rgba(60, 90, 90, 0.25)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 2,
                            backgroundColor: color,
                            boxShadow: scale.glowAmount ? `0 0 6px ${color}66` : "none",
                        },
                    }}
                />
            ) : null}
        </>
    );
}

const AGENT_STAT_ROWS = 5;

function visibleAgentRows(rows: BreakdownAgg[], selectedId?: string | null, limit = AGENT_STAT_ROWS) {
    const top = rows.slice(0, limit);
    if (!selectedId) return top;
    if (top.some((row) => row.id.toLowerCase() === selectedId.toLowerCase())) return top;
    const extra = rows.find((row) => row.id.toLowerCase() === selectedId.toLowerCase());
    if (!extra) return top;
    return [extra, ...top.slice(0, Math.max(0, limit - 1))];
}

function AgentStatsList({
    rows,
    neonColor,
    showAtk = false,
    showDef = false,
    selectedId,
    onRowClick,
    isMuted,
}: {
    rows: BreakdownAgg[];
    neonColor: string;
    showAtk?: boolean;
    showDef?: boolean;
    selectedId?: string | null;
    onRowClick?: (id: string) => void;
    isMuted?: (id: string) => boolean;
}) {
    const scale = useScale();
    const agentArt = 56;
    const agentSlot = AGENT_BG_INSET + agentArtSize(agentArt);
    const headers = [
        "G",
        "Win%",
        "RWin%",
        ...(showAtk ? ["Atk%"] : []),
        ...(showDef ? ["Def%"] : []),
        "ACS",
        "KD",
        "ADR",
        "Rating",
        "HS%",
        "FK/FD",
    ];
    const columns = [
        "32px",
        "minmax(44px, 1fr)",
        "minmax(44px, 1fr)",
        ...(showAtk ? ["minmax(40px, 0.9fr)"] : []),
        ...(showDef ? ["minmax(40px, 0.9fr)"] : []),
        "minmax(36px, 0.85fr)",
        "minmax(36px, 0.8fr)",
        "minmax(36px, 0.85fr)",
        "minmax(44px, 0.9fr)",
        "minmax(40px, 0.85fr)",
        "minmax(48px, 1fr)",
    ].join(" ");
    const gridSx = {
        flex: 1,
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: columns,
        columnGap: 1,
        alignItems: "center",
    } as const;
    const headerSx = {
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase" as const,
        color: "rgba(255,255,255,0.45)",
        textAlign: "center" as const,
    };
    const visibleRows = visibleAgentRows(rows, selectedId);

    return (
        <Stack spacing={0.75} sx={{ width: "100%" }}>
            <Stack direction="row" sx={{ alignItems: "center", px: 0.5 }}>
                <Box sx={{ width: agentSlot, minWidth: agentSlot, flexShrink: 0 }} />
                <Box sx={gridSx}>
                    {headers.map((h) => (
                        <Typography key={h} sx={headerSx}>
                            {h}
                        </Typography>
                    ))}
                </Box>
            </Stack>
            {visibleRows.map((row) => {
                const kd = row.deaths ? row.kills / row.deaths : row.kills;
                const acsColor = scale.color(row.acs, SCALE_STOPS.acs);
                const kdColor = scale.color(kd, SCALE_STOPS.kd);
                const adrColor = scale.color(row.adr, SCALE_STOPS.adr);
                const ratingColor = scale.ratingColor(row.rating);
                const hsColor = scale.color(hsUnit(row.hs), SCALE_STOPS.hs);
                const selected = Boolean(selectedId && selectedId.toLowerCase() === row.id.toLowerCase());
                const muted = Boolean(isMuted?.(row.id) && !selected);
                const accent = agentAccent(row.id) || scale.win;
                const wash = shadeHex(accent, 0.12);

                return (
                    <Box
                        key={row.id}
                        onClick={() => onRowClick?.(row.id)}
                        sx={{
                            position: "relative",
                            width: "100%",
                            borderRadius: 1,
                            overflow: "hidden",
                            borderLeft: `3px solid ${ratingColor}`,
                            cursor: onRowClick ? "pointer" : "default",
                            opacity: muted ? 0.38 : 1,
                            boxShadow: selected ? `0 0 18px ${neonColor}55, inset 0 0 24px ${neonColor}22` : "none",
                            transition: "filter 0.15s, opacity 0.15s, box-shadow 0.15s",
                            "&:hover": { filter: "brightness(1.1)" },
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 1,
                                backgroundColor: shadeHex(accent, 0.42),
                                mixBlendMode: "multiply",
                                opacity: 0.82,
                                pointerEvents: "none",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 1,
                                backgroundColor: accent,
                                mixBlendMode: "soft-light",
                                opacity: 0.28,
                                pointerEvents: "none",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 1,
                                background: `linear-gradient(90deg, rgba(0,0,0,0.92) 0%, ${wash}d1 35%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.42) 100%)`,
                                pointerEvents: "none",
                            }}
                        />
                        <FadedAgentArt agent={row.id} size={agentArt} asBackground />
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 2,
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                pr: 1.25,
                                pl: 0.5,
                                py: 0.5,
                            }}
                        >
                            <Box sx={{ width: agentSlot, minWidth: agentSlot, flexShrink: 0 }} />
                            <Box sx={gridSx}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                                    {row.games}
                                </Typography>
                                <Box sx={{ textAlign: "center" }}>
                                    <RateCell rate={row.winRate} />
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                    <RateCell rate={row.roundWinRate} />
                                </Box>
                                {showAtk ? (
                                    <Box sx={{ textAlign: "center" }}>
                                        <RateCell rate={row.atkWinRate} bar={false} />
                                    </Box>
                                ) : null}
                                {showDef ? (
                                    <Box sx={{ textAlign: "center" }}>
                                        <RateCell rate={row.defWinRate} bar={false} />
                                    </Box>
                                ) : null}
                                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)", ...scale.glow(acsColor) }}>
                                    {formatters.integer(row.acs)}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)", ...scale.glow(kdColor) }}>
                                    {formatters.float2(kd)}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)", ...scale.glow(adrColor) }}>
                                    {formatters.integer(row.adr)}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)", ...scale.glow(ratingColor) }}>
                                    {formatters.float2(row.rating)}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", textAlign: "center", textShadow: "0 1px 4px rgba(0,0,0,0.8)", ...scale.glow(hsColor) }}>
                                    {formatters.percent(hsUnit(row.hs))}
                                </Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", textAlign: "center", fontVariantNumeric: "tabular-nums", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                                    <Box component="span" sx={{ color: scale.win }}>
                                        {formatters.integer(row.firstKills)}
                                    </Box>
                                    <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", mx: 0.25 }}>
                                        /
                                    </Box>
                                    <Box component="span" sx={{ color: scale.loss }}>
                                        {formatters.integer(row.firstDeaths)}
                                    </Box>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}

function BreakdownStatsTable({
    rows,
    firstHeader,
    renderLabel,
    neonColor,
    showAtk = false,
    showDef = false,
    selectedId,
    onRowClick,
    isMuted,
}: {
    rows: BreakdownAgg[];
    firstHeader: string;
    renderLabel: (row: BreakdownAgg) => React.ReactNode;
    neonColor: string;
    showAtk?: boolean;
    showDef?: boolean;
    selectedId?: string | null;
    onRowClick?: (id: string) => void;
    isMuted?: (id: string) => boolean;
}) {
    const scale = useScale();
    const headers = [
        firstHeader,
        "G",
        "Win%",
        "RWin%",
        ...(showAtk ? ["Atk%"] : []),
        ...(showDef ? ["Def%"] : []),
        "ACS",
        "KD",
        "ADR",
        "Rating",
        "HS%",
        "FK/FD",
    ];
    const colWidths = [
        "18%",
        "6%",
        "9%",
        "9%",
        ...(showAtk ? ["8%"] : []),
        ...(showDef ? ["8%"] : []),
        "8%",
        "7%",
        "8%",
        "8%",
        "8%",
        "11%",
    ];

    const thSx = {
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase" as const,
        color: "rgba(255,255,255,0.45)",
        py: 1,
        px: 0.5,
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        whiteSpace: "nowrap" as const,
    };
    const tdSx = {
        py: 0.7,
        px: 0.4,
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
    };

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box
                component="table"
                sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                }}
            >
                <Box component="colgroup">
                    {colWidths.map((w, i) => (
                        <Box component="col" key={headers[i]} sx={{ width: w }} />
                    ))}
                </Box>
                <Box component="thead">
                    <Box component="tr">
                        {headers.map((h, i) => (
                            <Box
                                component="th"
                                key={h}
                                sx={{
                                    ...thSx,
                                    textAlign: i === 0 ? "left" : "center",
                                    ...(i === 0 ? { pl: 1.5 } : {}),
                                }}
                            >
                                {h}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map((row) => {
                        const kd = row.deaths ? row.kills / row.deaths : row.kills;
                        const acsColor = scale.color(row.acs, SCALE_STOPS.acs);
                        const kdColor = scale.color(kd, SCALE_STOPS.kd);
                        const adrColor = scale.color(row.adr, SCALE_STOPS.adr);
                        const ratingColor = scale.color(row.rating || null, SCALE_STOPS.rating);
                        const hsColor = scale.color(hsUnit(row.hs), SCALE_STOPS.hs);
                        const selected = Boolean(selectedId && selectedId.toLowerCase() === row.id.toLowerCase());
                        const muted = Boolean(isMuted?.(row.id) && !selected);

                        return (
                            <Box
                                component="tr"
                                key={row.id}
                                onClick={() => onRowClick?.(row.id)}
                                sx={{
                                    cursor: onRowClick ? "pointer" : "default",
                                    opacity: muted ? 0.38 : 1,
                                    backgroundColor: selected ? `${neonColor}18` : "transparent",
                                    boxShadow: selected ? `inset 3px 0 0 ${neonColor}` : "none",
                                    transition: "background-color 0.15s, opacity 0.15s",
                                    "&:hover td": {
                                        backgroundColor: selected ? `${neonColor}22` : "rgba(255,255,255,0.04)",
                                    },
                                    "&:last-child td": { borderBottom: "none" },
                                }}
                            >
                                <Box component="td" sx={{ ...tdSx, textAlign: "left", pl: 1.5 }}>
                                    {renderLabel(row)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {row.games}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    <RateCell rate={row.winRate} />
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    <RateCell rate={row.roundWinRate} />
                                </Box>
                                {showAtk ? (
                                    <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                        <RateCell rate={row.atkWinRate} bar={false} />
                                    </Box>
                                ) : null}
                                {showDef ? (
                                    <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                        <RateCell rate={row.defWinRate} bar={false} />
                                    </Box>
                                ) : null}
                                <Box component="td" sx={{ ...tdSx, textAlign: "center", ...scale.glow(acsColor) }}>
                                    {formatters.integer(row.acs)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center", ...scale.glow(kdColor) }}>
                                    {formatters.float2(kd)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center", ...scale.glow(adrColor) }}>
                                    {formatters.integer(row.adr)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center", ...scale.glow(ratingColor) }}>
                                    {formatters.float2(row.rating)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center", ...scale.glow(hsColor) }}>
                                    {formatters.percent(hsUnit(row.hs))}
                                </Box>
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    <Box component="span" sx={{ color: "#81c784" }}>
                                        {formatters.integer(row.firstKills)}
                                    </Box>
                                    <Box component="span" sx={{ color: "rgba(255,255,255,0.35)", mx: 0.25 }}>
                                        /
                                    </Box>
                                    <Box component="span" sx={{ color: "#e57373" }}>
                                        {formatters.integer(row.firstDeaths)}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

function riotNameParts(raw?: string | null) {
    const value = (raw || "").trim();
    const hash = value.lastIndexOf("#");
    if (hash <= 0) return { name: value, tag: "" };
    return { name: value.slice(0, hash).trim(), tag: value.slice(hash + 1).trim() };
}

function accountDisplayName(row: AccountRow) {
    const { name, tag } = riotNameParts(row.lastIgName || row.account.account_name);
    return { name: name || "Unknown", tag };
}

function AccountCard({
    row,
    selected,
    muted,
    neonColor,
    onClick,
}: {
    row: AccountRow;
    selected?: boolean;
    muted?: boolean;
    neonColor: string;
    onClick?: () => void;
}) {
    const ctx = useReferentialContext();
    const tier = useCompetitiveTier(row.rankTier);
    const palette = rankPalette(tier, row.rankTier);
    const { name, tag } = accountDisplayName(row);
    const portrait = agentFullPortraitUrl(row.mainAgent, ctx.agentsReferential);

    return (
        <Box
            onClick={onClick}
            sx={{
                position: "relative",
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 168,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                pt: 1.35,
                pb: 1.1,
                borderRadius: 1,
                overflow: "hidden",
                textAlign: "center",
                cursor: "pointer",
                opacity: muted ? 0.38 : 1,
                background: selected ? `${neonColor}18` : "rgba(255,255,255,0.035)",
                border: selected ? `1px solid ${neonColor}55` : "1px solid rgba(255,255,255,0.08)",
                boxShadow: selected ? `inset 0 2px 0 ${neonColor}` : "none",
                transition: "background-color 0.15s, opacity 0.15s, border-color 0.15s",
                "&:hover": {
                    background: selected ? `${neonColor}22` : "rgba(255,255,255,0.06)",
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `linear-gradient(180deg, ${palette.wash} 0%, rgba(8,10,14,0.42) 48%, rgba(8,10,14,0.82) 100%)`,
                    pointerEvents: "none",
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    zIndex: 3,
                    height: 2,
                    background: selected ? neonColor : palette.accent,
                },
            }}
        >
            {portrait ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                        src={portrait}
                        alt=""
                        sx={{
                            position: "absolute",
                            left: "22%",
                            top: "50%",
                            height: "130%",
                            width: "auto",
                            maxWidth: "none",
                            transform: "translate(-50%, -50%)",
                            objectFit: "contain",
                            filter: "brightness(1.08) saturate(1.08)",
                            userSelect: "none",
                        }}
                    />
                </Box>
            ) : null}
            <Box sx={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <RankIcon tier={row.rankTier} rating={row.account.ranked_rating} size={48} />
                <Typography
                    sx={{
                        mt: 0.35,
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        lineHeight: 1.2,
                        color: palette.accent,
                        whiteSpace: "nowrap",
                    }}
                >
                    {rankLabel(tier) || fullRankFromNumber(row.rankTier)}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.66rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: "rgba(255,255,255,0.5)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {row.account.ranked_rating != null ? `${row.account.ranked_rating} RR` : "—"}
                </Typography>
            </Box>

            <Box sx={{ position: "relative", zIndex: 2, width: "100%", minWidth: 0, mt: 0.15 }}>
                <Stack
                    direction="row"
                    spacing={0.4}
                    sx={{ alignItems: "baseline", justifyContent: "center", minWidth: 0 }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textShadow: "0 1px 8px rgba(0,0,0,0.85)",
                        }}
                    >
                        {name || "Unknown"}
                    </Typography>
                    {tag ? (
                        <Typography
                            sx={{
                                fontSize: "0.66rem",
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.42)",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            #{tag}
                        </Typography>
                    ) : null}
                </Stack>
            </Box>

            {row.account.server ? (
                <Chip
                    label={row.account.server.toUpperCase()}
                    size="small"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        height: 18,
                        fontSize: "0.58rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        color: "rgba(255,255,255,0.75)",
                        backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                />
            ) : null}
        </Box>
    );
}

const ACCOUNT_SLOTS = 3;

function EmptyAccountSlot() {
    return (
        <Box
            sx={{
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 168,
                borderRadius: 1,
                border: "1px dashed rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.02)",
            }}
        />
    );
}

function asAccountList(result: any): SoloQAccounts[] {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.results)) return result.results;
    return [];
}

function normalizeNameToken(value: string) {
    return value.trim().toLowerCase().split("#")[0].replace(/[^a-z0-9]+/g, "");
}

function playerNameTokens(player: Player) {
    const raw = [player.id, player.previous_names || ""]
        .flatMap((value) => value.split(/[,;/|]/))
        .map((value) => value.trim())
        .filter((value) => value.length >= 2);
    const seen = new Set<string>();
    const tokens: string[] = [];
    for (const value of raw) {
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        tokens.push(value);
    }
    return tokens;
}

function accountMatchScore(accountName: string, tokens: string[]) {
    const account = normalizeNameToken(accountName);
    if (account.length < 2) return 0;
    let best = 0;
    for (const token of tokens) {
        const candidate = normalizeNameToken(token);
        if (!candidate) continue;
        if (account === candidate) best = Math.max(best, 100);
        else if (account.startsWith(candidate) || candidate.startsWith(account)) best = Math.max(best, 70);
        else if (account.includes(candidate) || candidate.includes(account)) best = Math.max(best, 50);
    }
    return best;
}

function findSimilarPlayer(accountName: string, players: Player[]) {
    let best: { player: Player; score: number } | null = null;
    for (const player of players) {
        const score = accountMatchScore(accountName, playerNameTokens(player));
        if (score > 0 && (!best || score > best.score)) best = { player, score };
    }
    return best && best.score >= 50 ? best.player : null;
}

function soloqProfileHref(playerId: string) {
    return `/soloq-player-profile?player=${encodeURIComponent(playerId)}`;
}

function SuggestedAccountsPanel({
    accounts,
    loading,
    neonColor,
    onLink,
}: {
    accounts: SoloQAccounts[];
    loading: boolean;
    neonColor: string;
    onLink: (account: SoloQAccounts) => void;
}) {
    const { isPending } = usePlayerLinkQueue();
    return (
        <Stack spacing={1.25} sx={{ py: 1.5, px: 0.5 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center", fontWeight: 700 }}>
                No SoloQ account linked for this player.
            </Typography>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                    <CircularProgress size={22} sx={{ color: neonColor }} />
                </Box>
            ) : accounts.length ? (
                <>
                    <Typography sx={{ color: "rgba(255,255,255,0.45)", textAlign: "center", fontSize: "0.72rem" }}>
                        Possible matching accounts
                    </Typography>
                    <Stack spacing={0.75}>
                        {accounts.map((account) => {
                            const busy = Boolean(account.puuid && isPending(account.puuid));
                            return (
                                <Stack
                                    key={account.puuid || account.id}
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: "center",
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        background: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <RankIcon tier={account.rank_tier} rating={account.ranked_rating} size={28} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "0.82rem",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {account.account_name || "Unknown"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)" }}>
                                            {account.server ? account.server.toUpperCase() : "Unlinked"}
                                            {account.ranked_rating != null ? ` · ${account.ranked_rating} RR` : ""}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        disabled={busy}
                                        onClick={() => onLink(account)}
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: "none",
                                            minHeight: 30,
                                            px: 1.25,
                                            border: `1px solid ${neonColor}66`,
                                            color: "#fff",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {busy ? "Queued" : "Link"}
                                    </Button>
                                </Stack>
                            );
                        })}
                    </Stack>
                </>
            ) : (
                <Typography sx={{ color: "rgba(255,255,255,0.45)", textAlign: "center", fontSize: "0.72rem" }}>
                    No matching unlinked accounts found.
                </Typography>
            )}
        </Stack>
    );
}

function AccountCarousel({
    rows,
    start,
    neonColor,
    selectedAccountId,
    onSelect,
}: {
    rows: AccountRow[];
    start: number;
    neonColor: string;
    selectedAccountId: string | null;
    onSelect: (id: string) => void;
}) {
    const visible = rows.slice(start, start + ACCOUNT_SLOTS);
    const slots: Array<AccountRow | null> = [
        ...visible,
        ...Array.from({ length: Math.max(0, ACCOUNT_SLOTS - visible.length) }, () => null),
    ];

    return (
        <Box sx={{ width: "100%", display: "flex", gap: 0.75 }}>
            {slots.map((row, index) => {
                if (!row) return <EmptyAccountSlot key={`empty-${index}`} />;
                const id = accountKey(row.account);
                return (
                    <AccountCard
                        key={id || row.account.id}
                        row={row}
                        neonColor={neonColor}
                        selected={Boolean(
                            selectedAccountId && id.toLowerCase() === selectedAccountId.toLowerCase(),
                        )}
                        muted={Boolean(
                            selectedAccountId && id.toLowerCase() !== selectedAccountId.toLowerCase(),
                        )}
                        onClick={() => onSelect(id)}
                    />
                );
            })}
        </Box>
    );
}

function MapStatCard({
    stats,
    neonColor,
    mapRef,
    selected,
    onSelect,
}: {
    stats: MapStatsRow;
    neonColor: string;
    mapRef?: Maps;
    selected: boolean;
    onSelect: () => void;
}) {
    const scale = useScale();
    const [imgSrc, setImgSrc] = useState(mapArtUrl(stats.map, mapRef));

    useEffect(() => {
        setImgSrc(mapArtUrl(stats.map, mapRef));
    }, [stats.map, mapRef?.uuid, mapRef?.splash]);

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 148,
                minWidth: 0,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                border: selected ? `2px solid ${neonColor}` : "1px solid rgba(255,255,255,0.12)",
                boxShadow: selected ? `0 0 18px ${neonColor}55, inset 0 0 24px ${neonColor}22` : "none",
                backgroundColor: "rgba(255,255,255,0.03)",
                transition: "border-color 0.2s, box-shadow 0.2s, filter 0.2s",
                "&:hover": {
                    filter: "brightness(1.08)",
                    borderColor: selected ? neonColor : "rgba(255,255,255,0.28)",
                },
            }}
        >
            <Box
                component="img"
                src={imgSrc}
                alt={stats.map}
                onError={() => setImgSrc(mapBannerUrl(stats.map, mapRef))}
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.96) 100%)",
                }}
            />
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    height: "100%",
                    justifyContent: "flex-end",
                    p: 0.75,
                    gap: 0.3,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        textTransform: "capitalize",
                        letterSpacing: 0.15,
                        lineHeight: 1.1,
                        textShadow: "0 1px 8px rgba(0,0,0,0.9)",
                    }}
                >
                    {stats.map}
                </Typography>
                {(
                    [
                        ["Games", { rate: stats.winRate, wins: stats.wins, losses: stats.losses }],
                        ["Round", stats.round],
                        ["Pistol", stats.pistol],
                        ["Atk", stats.atk],
                        ["Def", stats.def],
                    ] as const
                ).map(([label, detail]) => {
                    const rate = detail.rate;
                    const color = scale.color(rate, SCALE_STOPS.rate);
                    const record =
                        detail.wins !== null && detail.losses !== null ? `${detail.wins}W–${detail.losses}L` : "—";
                    return (
                        <Box key={label}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "40px minmax(0, 1fr) 36px",
                                    columnGap: 0.5,
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "0.62rem",
                                        color: "rgba(255,255,255,0.55)",
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums",
                                        textAlign: "right",
                                    }}
                                >
                                    {record}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "0.68rem",
                                        ...(rate === null
                                            ? { color, fontVariantNumeric: "tabular-nums" }
                                            : scale.glow(color)),
                                        textAlign: "right",
                                    }}
                                >
                                    {rate === null ? "—" : formatters.percent(rate)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={rate === null ? 0 : Math.max(rate * 100, 4)}
                                sx={{
                                    mt: 0.15,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 2,
                                        backgroundColor: color,
                                        boxShadow: rate === null || !scale.glowAmount ? "none" : `0 0 8px ${color}88`,
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

function MatchStat({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) {
    return (
        <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    lineHeight: 1.15,
                    color: color || "rgba(255,255,255,0.92)",
                    textShadow: color
                        ? `0 0 8px ${color}55, 0 1px 4px rgba(0,0,0,0.85)`
                        : "0 1px 4px rgba(0,0,0,0.8)",
                    whiteSpace: "nowrap",
                }}
            >
                {value}
            </Typography>
            <Typography
                sx={{
                    mt: 0.15,
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.5)",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

const SCOREBOARD_COLUMNS = "minmax(206px, 2.3fr) 26px 26px 26px 36px 36px 40px 24px 24px 40px";
const SCOREBOARD_HEADERS = ["Player", "K", "D", "A", "ACS", "ADR", "KAST", "FK", "FD", "RT"];
const SCOREBOARD_AGENT_SLOT = 48;
const SCOREBOARD_RANK_SIZE = 24;
const SCOREBOARD_TEAM_SIZE = 28;

const PlayerMenuContainer = styled(Box)(({ theme }) => ({
    width: "260px",
    background: "rgba(5, 5, 15, 0.98)",
    borderRadius: "16px",
    border: `1px solid ${getNeonColor(theme)}25`,
    boxShadow: `
        0px 25px 80px rgba(0, 0, 0, 0.8),
        0px 0px 40px ${getNeonColor(theme)}15,
        inset 0px 1px 0px rgba(255, 255, 255, 0.05)
    `,
    backdropFilter: "blur(20px)",
    overflow: "hidden",
    position: "relative",
    padding: "16px",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "1px",
        background: `linear-gradient(90deg, 
            transparent 0%, 
            ${getNeonColor(theme)}60 30%,
            ${getNeonColor(theme)} 50%,
            ${getNeonColor(theme)}60 70%,
            transparent 100%)`,
        boxShadow: `0px 0px 20px ${getNeonColor(theme)}`,
    },
}));

const PlayerMenuItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
    padding: "10px 14px",
    marginBottom: "4px",
    borderRadius: "8px",
    cursor: disabled ? "default" : "pointer",
    color: disabled ? "rgba(255, 255, 255, 0.28)" : "rgba(255, 255, 255, 0.75)",
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.3px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    pointerEvents: disabled ? "none" : "auto",
    "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "50%",
        transform: "translateY(-50%) scaleY(0)",
        width: "3px",
        height: "60%",
        background: getNeonColor(theme),
        borderRadius: "0 4px 4px 0",
        transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: `0px 0px 8px ${getNeonColor(theme)}`,
    },
    "&:hover": disabled
        ? undefined
        : {
              background: `linear-gradient(135deg, 
            ${getNeonColor(theme)}12 0%, 
            ${getNeonColor(theme)}20 50%, 
            ${getNeonColor(theme)}12 100%)`,
              color: getNeonColor(theme),
              paddingLeft: "20px",
              textShadow: `0px 0px 15px ${getNeonColor(theme)}60`,
              boxShadow: `
            0px 0px 20px ${getNeonColor(theme)}15,
            inset 0px 0px 15px ${getNeonColor(theme)}08
        `,
              "&::before": {
                  transform: "translateY(-50%) scaleY(1)",
              },
          },
    "&:active": disabled ? undefined : { transform: "scale(0.98)" },
}));

function uniqueByPuuid(rows: SoloQGameSummaries[]) {
    const seen = new Set<string>();
    return rows.filter((row) => {
        const key = row.puuid || row.pk;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function splitTeams(players: SoloQGameSummaries[], focus: SoloQGameSummaries) {
    const unique = uniqueByPuuid(players);
    const byResult = {
        allies: unique.filter((p) => p.win === focus.win),
        enemies: unique.filter((p) => p.win !== focus.win),
    };
    if (byResult.allies.length > 0 && byResult.enemies.length > 0) return byResult;
    return {
        allies: unique.filter((p) => p.team === focus.team),
        enemies: unique.filter((p) => p.team !== focus.team),
    };
}

function StatCell({ value, suffix, bold, color, digits }: { value: number | null | undefined; suffix?: string; bold?: boolean; color?: string; digits?: number }) {
    const sx = {
        fontSize: "0.72rem",
        textAlign: "center" as const,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };
    if (value === null || value === undefined) {
        return <Typography sx={{ ...sx, color: "rgba(255,255,255,0.35)" }}>-</Typography>;
    }
    const display = Number.isInteger(value) ? String(value) : value.toFixed(digits ?? (suffix ? 0 : 1));
    return (
        <Typography sx={{ ...sx, fontWeight: bold ? 800 : 600, color: color || "rgba(255,255,255,0.9)" }}>
            {display}{suffix ?? ""}
        </Typography>
    );
}

function ScoreboardRow({
    children,
    highlighted,
    header,
}: {
    children: React.ReactNode;
    highlighted?: boolean;
    header?: boolean;
}) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: SCOREBOARD_COLUMNS,
                alignItems: "stretch",
                columnGap: 0.5,
                px: 0.5,
                py: header ? 0.25 : 0,
                borderRadius: 0.5,
                backgroundColor: highlighted ? "rgba(255,255,255,0.1)" : "transparent",
                boxShadow: highlighted ? "inset 2px 0 0 rgba(255,255,255,0.7)" : "none",
            }}
        >
            {children}
        </Box>
    );
}

function ScoreboardHeader() {
    return (
        <ScoreboardRow header>
            {SCOREBOARD_HEADERS.map((h, i) => (
                <Typography
                    key={`${h}-${i}`}
                    sx={{
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        color: "rgba(255,255,255,0.4)",
                        textAlign: i === 0 ? "left" : "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: i === 0 ? "flex-start" : "center",
                        pl: i === 0 ? `${SCOREBOARD_AGENT_SLOT + 8}px` : 0,
                    }}
                >
                    {h}
                </Typography>
            ))}
        </ScoreboardRow>
    );
}

function LinkPlayerDialog({
    open,
    accountName,
    puuid,
    currentPlayerId,
    onClose,
    onLinked,
}: {
    open: boolean;
    accountName: string;
    puuid: string;
    currentPlayerId?: string;
    onClose: () => void;
    onLinked: (playerId: string) => void;
}) {
    const ctx = useReferentialContext();
    const { enqueuePlayerLink, isPending } = usePlayerLinkQueue();
    const suggestedPlayer = useMemo(() => {
        if (currentPlayerId) {
            const current = ctx.playersReferential.find(
                (entry) => entry.id.toLowerCase() === currentPlayerId.toLowerCase(),
            );
            if (current) return current;
        }
        return findSimilarPlayer(accountName, ctx.playersReferential);
    }, [accountName, currentPlayerId, ctx.playersReferential]);
    const [player, setPlayer] = useState<Player | null>(suggestedPlayer);
    const [error, setError] = useState<string | null>(null);
    const userTouched = useRef(false);
    const queued = isPending(puuid);

    useEffect(() => {
        if (!open) {
            userTouched.current = false;
            return;
        }
        setError(null);
        if (!userTouched.current) setPlayer(suggestedPlayer);
    }, [open, suggestedPlayer]);

    const link = () => {
        if (!player || queued) return;
        setError(null);
        const previousId = currentPlayerId || "";
        const queuedOk = enqueuePlayerLink({
            playerId: player.id,
            puuid,
            accountName,
            onComplete: (ok) => {
                if (!ok) onLinked(previousId);
            },
        });
        if (!queuedOk) {
            setError("This account is already being linked.");
            return;
        }
        onLinked(player.id);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            scroll="body"
            PaperProps={{
                sx: (theme) => ({
                    height: "auto",
                    overflow: "visible",
                    background: "rgba(5, 5, 15, 0.98)",
                    borderRadius: "16px",
                    border: `1px solid ${getNeonColor(theme)}25`,
                    boxShadow: `
                        0px 25px 80px rgba(0, 0, 0, 0.8),
                        0px 0px 40px ${getNeonColor(theme)}15,
                        inset 0px 1px 0px rgba(255, 255, 255, 0.05)
                    `,
                }),
            }}
        >
            <DialogTitle
                sx={(theme) => ({
                    color: getNeonColor(theme),
                    fontWeight: 700,
                    fontSize: "1rem",
                    pb: 1,
                })}
            >
                Link player
            </DialogTitle>
            <DialogContent sx={{ overflow: "visible", pt: "8px !important", pb: 1 }}>
                <Stack spacing={1.5}>
                    <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem" }}>
                        Link {accountName || "this account"} to a competitive player.
                    </Typography>
                    <Autocomplete
                        size="small"
                        options={ctx.playersReferential}
                        value={player}
                        onChange={(_event, value) => {
                            userTouched.current = true;
                            setPlayer(value);
                            setError(null);
                        }}
                        disabled={queued}
                        getOptionLabel={(option) => option.id}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(options, state) => {
                            const query = state.inputValue.trim().toLowerCase();
                            if (query.length < 1) return [];
                            return options.filter((entry) => entry.id.toLowerCase().startsWith(query)).slice(0, 40);
                        }}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
                                    {option.current_team || option.last_team ? (
                                        <TeamIcon
                                            team_name={option.current_team || option.last_team || ""}
                                            sx={{ width: 18, height: 18 }}
                                        />
                                    ) : null}
                                    <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {option.id}
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Player ID" autoFocus />
                        )}
                        slotProps={{
                            popper: { sx: { zIndex: 1500 } },
                        }}
                    />
                    {error ? (
                        <Typography sx={{ fontSize: "0.75rem", color: "#f87171" }}>{error}</Typography>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button onClick={link} disabled={!player || queued} sx={{ textTransform: "none", fontWeight: 700 }}>
                    {queued ? "Queued" : "Link"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ScoreboardPlayerName({
    row,
    highlighted,
    onPlayerLinked,
}: {
    row: SoloQGameSummaries;
    highlighted: boolean;
    onPlayerLinked?: (puuid: string, playerId: string) => void;
}) {
    const navigate = useNavigate();
    const { playersReferential } = useReferentialContext();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const playerId = (row.competitive_player || "").trim();
    const accountName = row.ig_name || "Unknown";
    const player = playerId
        ? playersReferential.find((p) => p.id.toLowerCase() === playerId.toLowerCase())
        : undefined;
    const teamName = player?.current_team || player?.last_team || "";
    const ellipsis = {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
    } as const;
    const canLink = Boolean(row.puuid);
    const canOpenProfile = Boolean(playerId);

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
    };

    const closeMenu = () => setMenuAnchor(null);
    const menuOpen = Boolean(menuAnchor);

    return (
        <Box sx={{ minWidth: 0, height: "100%" }}>
            <Box
                onClick={openMenu}
                sx={(theme) => {
                    const neon = getNeonColor(theme);
                    return {
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        width: "100%",
                        height: "100%",
                        minWidth: 0,
                        minHeight: 44,
                        px: 0.5,
                        py: 0.25,
                        cursor: "pointer",
                        borderRadius: 1,
                        background: menuOpen
                            ? `linear-gradient(135deg, ${neon}12 0%, ${neon}20 50%, ${neon}12 100%)`
                            : "transparent",
                        boxShadow: menuOpen ? `inset 0 0 12px ${neon}10` : "none",
                        transition: "background 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                            background: `linear-gradient(135deg, ${neon}12 0%, ${neon}22 50%, ${neon}12 100%)`,
                            boxShadow: `inset 0 0 14px ${neon}12`,
                        },
                    };
                }}
            >
                <FadedAgentArt agent={row.agent} size={40} />
                <RankIcon tier={row.rank_tier} size={SCOREBOARD_RANK_SIZE} sx={{ flexShrink: 0 }} />
                <Box
                    sx={{
                        width: SCOREBOARD_TEAM_SIZE,
                        minWidth: SCOREBOARD_TEAM_SIZE,
                        height: SCOREBOARD_TEAM_SIZE,
                        flexShrink: 0,
                    }}
                >
                    {teamName ? (
                        <TeamIcon
                            team_name={teamName}
                            sx={{ width: SCOREBOARD_TEAM_SIZE, height: SCOREBOARD_TEAM_SIZE }}
                        />
                    ) : null}
                </Box>
                {playerId ? (
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: "0.68rem", fontWeight: highlighted ? 800 : 700, lineHeight: 1.15, ...ellipsis }}>
                            {playerId}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.58rem",
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.45)",
                                lineHeight: 1.2,
                                ...ellipsis,
                            }}
                        >
                            {accountName}
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: highlighted ? 800 : 600, flex: 1, ...ellipsis }}>
                        {accountName}
                    </Typography>
                )}
            </Box>
            <Menu
                MenuListProps={{ disablePadding: true }}
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={closeMenu}
                onClick={(event) => event.stopPropagation()}
                sx={{ padding: 0, margin: 0 }}
                PaperProps={{
                    sx: {
                        backgroundColor: "transparent",
                        backgroundImage: "none",
                        boxShadow: "none",
                        overflow: "visible",
                    },
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <PlayerMenuContainer>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "20%",
                            left: "10%",
                            width: "200px",
                            height: "200px",
                            background: (theme) => getNeonColor(theme),
                            opacity: 0.04,
                            filter: "blur(60px)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                        }}
                    />
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                        <PlayerMenuItem
                            disabled={!canLink}
                            onClick={() => {
                                closeMenu();
                                setLinkOpen(true);
                            }}
                        >
                            Link player
                        </PlayerMenuItem>
                        <PlayerMenuItem
                            disabled={!canOpenProfile}
                            onClick={() => {
                                closeMenu();
                                if (playerId) navigate(soloqProfileHref(playerId));
                            }}
                        >
                            SoloQ profile
                        </PlayerMenuItem>
                    </Box>
                </PlayerMenuContainer>
            </Menu>
            {row.puuid ? (
                <LinkPlayerDialog
                    open={linkOpen}
                    accountName={accountName}
                    puuid={row.puuid}
                    currentPlayerId={playerId || undefined}
                    onClose={() => setLinkOpen(false)}
                    onLinked={(linkedId) => onPlayerLinked?.(row.puuid, linkedId)}
                />
            ) : null}
        </Box>
    );
}

function ScoreboardPlayerRow({
    row,
    highlighted,
    onPlayerLinked,
}: {
    row: SoloQGameSummaries;
    highlighted: boolean;
    onPlayerLinked?: (puuid: string, playerId: string) => void;
}) {
    const rating = row.rating_vlr ?? 0;
    const scale = useScale();
    return (
        <ScoreboardRow highlighted={highlighted}>
            <ScoreboardPlayerName row={row} highlighted={highlighted} onPlayerLinked={onPlayerLinked} />
            <StatCell value={row.kills} bold />
            <StatCell value={row.deaths} />
            <StatCell value={row.assists} />
            <StatCell value={row.acs} digits={0} />
            <StatCell value={row.adr} digits={0} />
            <StatCell value={row.kast != null ? row.kast * 100 : null} suffix="%" />
            <StatCell value={row.first_kills} />
            <StatCell value={row.first_deaths} />
            <Typography
                sx={{
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...scale.glow(scale.color(rating || null, SCALE_STOPS.rating)),
                }}
            >
                {row.rating_vlr != null ? row.rating_vlr.toFixed(2) : "-"}
            </Typography>
        </ScoreboardRow>
    );
}

function sameTeam(a: unknown, b: unknown) {
    if (a == null || b == null || a === "" || b === "") return false;
    return String(a) === String(b);
}

function roundWon(round: { win?: number | boolean | null }) {
    return round.win === 1 || round.win === true;
}

function allyRoundsForPlayer(
    rounds: SoloQTeamRoundSummaries[],
    allyTeam: SoloQGameSummaries["team"] | undefined,
    igName?: string,
) {
    let allyRounds = rounds
        .filter((r) => sameTeam(r.team, allyTeam))
        .sort((a, b) => a.round_index - b.round_index);
    if (allyRounds.length === 0 && igName) {
        const needle = igName.split("#")[0].trim().toLowerCase();
        if (needle) {
            allyRounds = rounds
                .filter((r) => (r.players || "").toLowerCase().includes(needle))
                .sort((a, b) => a.round_index - b.round_index);
        }
    }
    if (allyRounds.length === 0 && rounds.length > 0) {
        const team = rounds[0]?.team;
        allyRounds = rounds
            .filter((r) => sameTeam(r.team, team))
            .sort((a, b) => a.round_index - b.round_index);
        if (allyRounds.length === 0) {
            allyRounds = rounds.slice(0, Math.ceil(rounds.length / 2)).sort((a, b) => a.round_index - b.round_index);
        }
    }
    return allyRounds;
}

function startingSide(rounds: SoloQTeamRoundSummaries[]): "Attacker" | "Defender" | null {
    const pistol = rounds.find((r) => r.round_index === 0) || rounds[0];
    const side = (pistol?.side || "").toLowerCase();
    if (side.startsWith("attack")) return "Attacker";
    if (side.startsWith("defend")) return "Defender";
    return null;
}

function TeamBlock({
    title,
    won,
    rows,
    focusPuuid,
    titleColor,
    onPlayerLinked,
}: {
    title: string;
    won: boolean;
    rows: SoloQGameSummaries[];
    focusPuuid: string;
    titleColor?: string;
    onPlayerLinked?: (puuid: string, playerId: string) => void;
}) {
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", px: 0.5, mb: 0.35 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", letterSpacing: "0.04em", color: titleColor }}>
                    {title}
                </Typography>
                <Chip
                    label={won ? "WIN" : "LOSS"}
                    size="small"
                    sx={{
                        height: 16,
                        fontSize: "0.58rem",
                        fontWeight: 800,
                        backgroundColor: won ? "rgba(76,175,80,0.22)" : "rgba(244,67,54,0.22)",
                        color: won ? "#81c784" : "#e57373",
                    }}
                />
            </Stack>
            <ScoreboardHeader />
            {rows.map((row) => (
                <ScoreboardPlayerRow
                    key={row.pk || row.puuid}
                    row={row}
                    highlighted={row.puuid === focusPuuid}
                    onPlayerLinked={onPlayerLinked}
                />
            ))}
        </Box>
    );
}

function RoundTimeline({
    rounds,
    allyTeam,
    igName,
}: {
    rounds: SoloQTeamRoundSummaries[];
    allyTeam: SoloQGameSummaries["team"];
    igName?: string;
}) {
    const allyRounds = allyRoundsForPlayer(rounds, allyTeam, igName);
    if (allyRounds.length === 0) return null;

    const allyStart = startingSide(allyRounds);
    const firstHalfSide = allyStart === "Attacker" ? "Attackers" : allyStart === "Defender" ? "Defenders" : null;
    const secondHalfSide = firstHalfSide === "Attackers" ? "Defenders" : firstHalfSide === "Defenders" ? "Attackers" : null;

    const hasHalves = allyRounds.length > 12;
    const firstHalfCount = Math.min(allyRounds.length, 12);
    const secondHalfCount = Math.max(allyRounds.length - 12, 0);

    const roundCell = (r: SoloQTeamRoundSummaries, row: 1 | 2) => {
        const won = roundWon(r);
        if ((row === 1 && !won) || (row === 2 && won)) return null;
        return (
            <Tooltip title={`R${r.round_index + 1} · ${r.side || ""} · ${won ? "Won" : "Lost"}`}>
                <Box
                    sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: won ? "rgba(46, 204, 113, 0.22)" : "rgba(231, 76, 60, 0.22)",
                        border: `1.5px solid ${won ? "rgba(46, 204, 113, 0.5)" : "rgba(231, 76, 60, 0.5)"}`,
                    }}
                >
                    <RoundEndIcon round_end={r.round_end ?? undefined} sx={{ width: 15, height: 15 }} />
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `28px repeat(${firstHalfCount}, 22px)${hasHalves ? " 16px" : ""}${secondHalfCount ? ` repeat(${secondHalfCount}, 22px)` : ""}`,
                    gridTemplateRows: firstHalfSide ? "14px 22px 22px" : "22px 22px",
                    columnGap: "4px",
                    rowGap: "4px",
                    alignItems: "center",
                    flexShrink: 0,
                    px: 0.5,
                }}
            >
            {firstHalfSide && (
                <Typography
                    sx={{
                        gridColumn: `2 / ${firstHalfCount + 2}`,
                        gridRow: 1,
                        fontSize: "0.56rem",
                        fontWeight: 800,
                        letterSpacing: "0.06em",
                        color: firstHalfSide === "Attackers" ? "#e57373" : "#80cbc4",
                        textAlign: "center",
                        lineHeight: 1,
                    }}
                >
                    {firstHalfSide}
                </Typography>
            )}
            {firstHalfSide && hasHalves && secondHalfSide && (
                <Typography
                    sx={{
                        gridColumn: `${firstHalfCount + 3} / -1`,
                        gridRow: 1,
                        fontSize: "0.56rem",
                        fontWeight: 800,
                        letterSpacing: "0.06em",
                        color: secondHalfSide === "Attackers" ? "#e57373" : "#80cbc4",
                        textAlign: "center",
                        lineHeight: 1,
                    }}
                >
                    {secondHalfSide}
                </Typography>
            )}
            <Typography sx={{ gridColumn: 1, gridRow: firstHalfSide ? 2 : 1, fontSize: "0.58rem", fontWeight: 800, color: "#81c784", letterSpacing: "0.04em" }}>
                WIN
            </Typography>
            <Typography sx={{ gridColumn: 1, gridRow: firstHalfSide ? 3 : 2, fontSize: "0.58rem", fontWeight: 800, color: "#e57373", letterSpacing: "0.04em" }}>
                LOSS
            </Typography>
            {hasHalves && (
                <Tooltip title="Side swap">
                    <Box sx={{ gridColumn: firstHalfCount + 2, gridRow: firstHalfSide ? "2 / 4" : "1 / 3", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <SwapVert sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }} />
                    </Box>
                </Tooltip>
            )}
            {allyRounds.map((r, i) => {
                const col = i < 12 ? i + 2 : i + 3;
                const won = roundWon(r);
                const row = firstHalfSide ? (won ? 2 : 3) : (won ? 1 : 2);
                return (
                    <Box
                        key={r.id || `${r.round_index}-${i}`}
                        sx={{ gridColumn: col, gridRow: row, width: 22, height: 22 }}
                    >
                        {roundCell(r, won ? 1 : 2)}
                    </Box>
                );
            })}
            </Box>
        </Box>
    );
}

function useMatchDetails(gameId: string | undefined, active: boolean) {
    const ctx = useReferentialContext();
    const loadedFor = useRef<string | null>(null);
    const [players, setPlayers] = useState<SoloQGameSummaries[] | null>(null);
    const [rounds, setRounds] = useState<SoloQTeamRoundSummaries[] | null>(null);
    const [status, setStatus] = useState<"idle" | "loading" | "ready">("idle");

    useEffect(() => {
        if (!active || !gameId) return;
        if (loadedFor.current === gameId) return;
        let cancelled = false;
        setStatus("loading");
        const loadRounds = async () => {
            try {
                const byGame = await ctx.reckonApi.soloQTeamRoundSummaries.list({
                    game: gameId,
                    ordering: ["round_index"],
                });
                if (byGame?.length) return byGame;
            } catch {
                /* try additional_filters next */
            }
            try {
                return (
                    (await ctx.reckonApi.soloQTeamRoundSummaries.list({
                        additional_filters: { game: gameId },
                        ordering: ["round_index"],
                    })) ?? []
                );
            } catch {
                return [];
            }
        };
        Promise.all([
            ctx.reckonApi.soloqGameSummaries.list({
                game_id: gameId,
                ordering: ["-acs"],
            }),
            loadRounds(),
        ])
            .then(([sums, rnds]) => {
                if (cancelled) return;
                loadedFor.current = gameId;
                setPlayers(sums ?? []);
                setRounds(rnds ?? []);
                setStatus("ready");
            })
            .catch(() => {
                if (cancelled) return;
                loadedFor.current = gameId;
                setPlayers([]);
                setRounds([]);
                setStatus("ready");
            });
        return () => {
            cancelled = true;
        };
    }, [gameId, active]);

    const linkPlayer = useCallback((puuid: string, playerId: string) => {
        setPlayers((prev) =>
            prev
                ? prev.map((row) => (row.puuid === puuid ? { ...row, competitive_player: playerId } : row))
                : prev,
        );
    }, []);

    return { players, rounds, status, linkPlayer };
}

const MATCH_DETAILS_HEIGHT = 352;

function MatchDetails({
    summary,
    players,
    rounds,
    onPlayerLinked,
}: {
    summary: SoloQGameSummaries;
    players: SoloQGameSummaries[];
    rounds: SoloQTeamRoundSummaries[] | null;
    onPlayerLinked?: (puuid: string, playerId: string) => void;
}) {
    if (players.length === 0) {
        return (
            <Typography sx={{ color: "rgba(255,255,255,0.55)", textAlign: "center", py: 2, fontSize: "0.82rem" }}>
                No detailed stats available for this game.
            </Typography>
        );
    }

    const self =
        players.find((row) => row.puuid && row.puuid === summary.puuid) ||
        players.find((row) => row.ig_name && row.ig_name === summary.ig_name) ||
        summary;
    const { allies, enemies } = splitTeams(players, self);
    const sortByAcs = (rows: SoloQGameSummaries[]) => [...rows].sort((a, b) => (b.acs ?? 0) - (a.acs ?? 0));
    const won = Boolean(self.win);
    const allyStart = rounds ? startingSide(allyRoundsForPlayer(rounds, self.team, self.ig_name)) : null;
    const defenders = allyStart === "Defender" ? allies : allyStart === "Attacker" ? enemies : allies;
    const attackers = allyStart === "Defender" ? enemies : allyStart === "Attacker" ? allies : enemies;
    const defendersWon = allyStart === "Defender" ? won : allyStart === "Attacker" ? !won : won;
    const attackersWon = !defendersWon;

    return (
        <Stack spacing={1} sx={{ minWidth: 0, width: "100%", height: "100%", overflow: "auto" }}>
            {rounds && rounds.length > 0 && (
                <RoundTimeline rounds={rounds} allyTeam={self.team} igName={self.ig_name} />
            )}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ alignItems: "flex-start", minWidth: 0 }}
            >
                <TeamBlock
                    title={allyStart ? "Defenders" : "Allies"}
                    titleColor={allyStart ? "#80cbc4" : undefined}
                    won={defendersWon}
                    rows={sortByAcs(defenders)}
                    focusPuuid={summary.puuid}
                    onPlayerLinked={onPlayerLinked}
                />
                <TeamBlock
                    title={allyStart ? "Attackers" : "Enemies"}
                    titleColor={allyStart ? "#e57373" : undefined}
                    won={attackersWon}
                    rows={sortByAcs(attackers)}
                    focusPuuid={summary.puuid}
                    onPlayerLinked={onPlayerLinked}
                />
            </Stack>
        </Stack>
    );
}

function MatchHistoryRow({
    summary,
    mapRef,
    accountLabel,
}: {
    summary: SoloQGameSummaries;
    mapRef?: Maps;
    accountLabel?: string;
    neonColor?: string;
}) {
    const [open, setOpen] = useState(false);
    const scale = useScale();
    const won = Boolean(summary.win);
    const accent = won ? scale.win : scale.loss;
    const wash = won ? scale.winWash : scale.lossWash;
    const rating = summary.rating_vlr ?? 0;
    const ratingColor = scale.color(rating || null, SCALE_STOPS.rating);
    const hasScore = summary.rounds_won != null && summary.rounds_lost != null;
    const agentArt = 64;
    const agentSlot = AGENT_BG_INSET + agentArtSize(agentArt);
    const details = useMatchDetails(summary.game_id || summary.pk, open);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                borderLeft: `3px solid ${accent}`,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${mapArtUrl(summary.map, mapRef)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center 35%",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    backgroundColor: shadeHex(accent, 0.42),
                    mixBlendMode: "multiply",
                    opacity: 0.82,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    backgroundColor: accent,
                    mixBlendMode: "soft-light",
                    opacity: 0.28,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `linear-gradient(90deg, rgba(0,0,0,0.92) 0%, ${wash}d1 35%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.42) 100%)`,
                    pointerEvents: "none",
                }}
            />
            <Box
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    position: "relative",
                    zIndex: 2,
                    cursor: "pointer",
                    transition: "filter 0.15s",
                    "&:hover": { filter: "brightness(1.1)" },
                }}
            >
                <FadedAgentArt agent={summary.agent} size={agentArt} asBackground />
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        pr: 1.25,
                        pl: 0.5,
                        py: 0.5,
                    }}
                >
                    <Box sx={{ width: agentSlot, minWidth: agentSlot, flexShrink: 0 }} />
                    <AgentRoleMark
                        agent={summary.agent}
                        role={(summary as SoloQGameSummaries & { agent__role?: string }).agent__role}
                        roleIcon={(summary as SoloQGameSummaries & { agent__role_icon?: string }).agent__role_icon}
                    />
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "40px 56px 40px minmax(0, 1fr) auto 72px 18px",
                                md: "40px 64px 40px minmax(100px, 1fr) 76px 48px 52px 72px 18px",
                            },
                            columnGap: 1,
                            alignItems: "center",
                            pl: 0.5,
                        }}
                    >
                <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", fontWeight: 600 }}>
                    {dayjs(summary.date).format("HH:mm")}
                </Typography>

                <Box sx={{ textAlign: "center" }}>
                    {hasScore ? (
                        <Stack direction="row" spacing={0.35} sx={{ alignItems: "center", justifyContent: "center" }}>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "0.98rem",
                                    color: won ? scale.win : scale.loss,
                                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                }}
                            >
                                {summary.rounds_won}
                            </Typography>
                            <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem" }}>-</Typography>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "0.98rem",
                                    color: won ? scale.loss : scale.win,
                                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                }}
                            >
                                {summary.rounds_lost}
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.72rem",
                                color: won ? scale.win : scale.loss,
                            }}
                        >
                            {won ? "WIN" : "LOSS"}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <RankIcon tier={summary.rank_tier} size={36} />
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: "0.74rem", md: "0.8rem" },
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.88)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        textShadow: "0 1px 6px rgba(0,0,0,0.85)",
                    }}
                >
                    {accountLabel || "—"}
                </Typography>

                <MatchStat
                    label="K/D/A"
                    value={`${summary.kills ?? 0}/${summary.deaths ?? 0}/${summary.assists ?? 0}`}
                />
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MatchStat label="ACS" value={formatters.integer(summary.acs ?? 0)} />
                </Box>
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MatchStat label="Rating" value={formatters.float2(rating)} color={ratingColor} />
                </Box>
                <Chip
                    label={capitalize(summary.map || "—")}
                    size="small"
                    variant="outlined"
                    sx={{
                        width: "100%",
                        fontSize: "0.65rem",
                        height: 22,
                        borderColor: won ? `${scale.win}8c` : `${scale.loss}8c`,
                        color: won ? scale.win : scale.loss,
                        backgroundColor: "rgba(0,0,0,0.45)",
                        backdropFilter: "blur(4px)",
                        "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            px: 0.75,
                        },
                    }}
                />
                <KeyboardArrowDown
                    sx={{
                        fontSize: 18,
                        color: "rgba(255,255,255,0.55)",
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.25s",
                    }}
                />
                    </Box>
                </Box>
            </Box>
            <Collapse in={open} timeout={{ enter: 360, exit: 280 }}>
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        px: 1.25,
                        py: 1.1,
                        minHeight: MATCH_DETAILS_HEIGHT,
                        height: MATCH_DETAILS_HEIGHT,
                        overflow: "hidden",
                    }}
                >
                    {details.status !== "ready" ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={22} thickness={4} sx={{ color: "rgba(255,255,255,0.4)" }} />
                        </Box>
                    ) : null}
                    <Fade in={details.status === "ready"} timeout={320}>
                        <Box sx={{ height: "100%" }}>
                            {details.players ? (
                                <MatchDetails
                                    summary={summary}
                                    players={details.players}
                                    rounds={details.rounds}
                                    onPlayerLinked={details.linkPlayer}
                                />
                            ) : null}
                        </Box>
                    </Fade>
                </Box>
            </Collapse>
        </Box>
    );
}

function FilterSlot({ open, children }: { open: boolean; children: React.ReactNode }) {
    return (
        <Box
            aria-hidden={!open}
            sx={{
                display: "grid",
                gridTemplateRows: open ? "1fr" : "0fr",
                opacity: open ? 1 : 0,
                flex: "0 0 auto",
                width: "100%",
                transition:
                    "grid-template-rows 0.34s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease",
                pointerEvents: open ? "auto" : "none",
            }}
        >
            <Box sx={{ overflow: "hidden", minHeight: 0 }}>{children}</Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function PlayerProfile() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const { enqueuePlayerLink } = usePlayerLinkQueue();
    const [searchParams] = useSearchParams();
    const playerParam = searchParams.get("player");

    const [player, setPlayer] = useState<Player | null>(ctx.selectedPlayer);
    const [from, setFrom] = useState(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState(dayjs());
    const [accounts, setAccounts] = useState<SoloQAccounts[]>([]);
    const [accountsOwnerId, setAccountsOwnerId] = useState<string | null>(null);
    const [summaries, setSummaries] = useState<SoloQGameSummaries[]>([]);
    const [agents, setAgents] = useState<BreakdownAgg[]>([]);
    const [roles, setRoles] = useState<BreakdownAgg[]>([]);
    const [mapBreakdown, setMapBreakdown] = useState<BreakdownAgg[]>([]);
    const [mapRoundRates, setMapRoundRates] = useState(EMPTY_MAP_ROUND_RATES);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [suggestedAccounts, setSuggestedAccounts] = useState<SoloQAccounts[]>([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [focus, setFocus] = useState<BreakdownFilter | null>(null);
    const [accountStart, setAccountStart] = useState(0);
    const [statsView, setStatsView] = useState<"agents" | "maps">("agents");
    const accountsReq = useRef(0);
    const suggestionsReq = useRef(0);
    const statsReq = useRef(0);
    const playerRef = useRef(player);
    playerRef.current = player;

    useEffect(() => {
        if (playerParam) {
            const wanted = decodeURIComponent(playerParam).toLowerCase();
            const match = ctx.playersReferential.find((entry) => entry.id.toLowerCase() === wanted);
            if (match) setPlayer((current) => (current?.id === match.id ? current : match));
            return;
        }
        if (!player && ctx.selectedPlayer) setPlayer(ctx.selectedPlayer);
    }, [playerParam, ctx.playersReferential, ctx.selectedPlayer, player]);

    useEffect(() => {
        if (!player) {
            accountsReq.current += 1;
            statsReq.current += 1;
            setAccounts([]);
            setAccountsOwnerId(null);
            setSuggestedAccounts([]);
            setSuggestionsLoading(false);
            setSummaries([]);
            setAgents([]);
            setRoles([]);
            setMapBreakdown([]);
            setMapRoundRates(EMPTY_MAP_ROUND_RATES);
            setAccountsLoading(false);
            setStatsLoading(false);
            setBreakdownLoading(false);
            setFocus(null);
            setStatsView("agents");
            return;
        }

        const req = ++accountsReq.current;
        statsReq.current += 1;
        setAccountsLoading(true);
        setStatsLoading(true);
        setBreakdownLoading(true);
        setFocus(null);
        ctx.reckonApi.soloQAccounts
            .list({
                player: player.id,
                ordering: ["-rank_tier"],
            })
            .then((result) => {
                if (req !== accountsReq.current) return;
                setAccounts(result);
                setAccountsOwnerId(player.id);
                setAccountsLoading(false);
            })
            .catch(() => {
                if (req !== accountsReq.current) return;
                setAccounts([]);
                setAccountsOwnerId(player.id);
                setAccountsLoading(false);
            });
    }, [player?.id]);

    useEffect(() => {
        if (!player || accountsLoading || accountsOwnerId !== player.id) return;
        if (accounts.length > 0) {
            suggestionsReq.current += 1;
            setSuggestedAccounts([]);
            setSuggestionsLoading(false);
            return;
        }

        const req = ++suggestionsReq.current;
        const tokens = playerNameTokens(player);
        if (!tokens.length) {
            setSuggestedAccounts([]);
            setSuggestionsLoading(false);
            return;
        }

        setSuggestionsLoading(true);
        Promise.all(
            tokens.slice(0, 3).map((token) =>
                ctx.reckonApi.soloQAccounts
                    .list({
                        additional_filters: { account_name__icontains: token, player__isnull: true },
                        ordering: ["-rank_tier"],
                    })
                    .catch(() =>
                        ctx.reckonApi.soloQAccounts.list({
                            additional_filters: { account_name__icontains: token },
                            ordering: ["-rank_tier"],
                        }),
                    )
                    .catch(() => [] as SoloQAccounts[]),
            ),
        )
            .then((results) => {
                if (req !== suggestionsReq.current) return;
                const seen = new Set<string>();
                const matches: { account: SoloQAccounts; score: number }[] = [];
                for (const list of results) {
                    for (const account of asAccountList(list)) {
                        if (account.player) continue;
                        const key = account.puuid || String(account.id);
                        if (!key || seen.has(key)) continue;
                        const score = accountMatchScore(account.account_name, tokens);
                        if (score < 50) continue;
                        seen.add(key);
                        matches.push({ account, score });
                    }
                }
                matches.sort((a, b) => b.score - a.score || (b.account.rank_tier ?? 0) - (a.account.rank_tier ?? 0));
                setSuggestedAccounts(matches.slice(0, 6).map((entry) => entry.account));
                setSuggestionsLoading(false);
            })
            .catch(() => {
                if (req !== suggestionsReq.current) return;
                setSuggestedAccounts([]);
                setSuggestionsLoading(false);
            });
    }, [player, accountsLoading, accountsOwnerId, accounts.length]);

    const linkSuggestedAccount = (account: SoloQAccounts) => {
        const current = playerRef.current;
        if (!current || !account.puuid) return;
        enqueuePlayerLink({
            playerId: current.id,
            puuid: account.puuid,
            accountName: account.account_name,
            onComplete: (ok) => {
                if (!ok || playerRef.current?.id !== current.id) return;
                ctx.reckonApi.soloQAccounts
                    .list({
                        player: current.id,
                        ordering: ["-rank_tier"],
                    })
                    .then((result) => {
                        if (playerRef.current?.id !== current.id) return;
                        setAccounts(asAccountList(result));
                        setAccountsOwnerId(current.id);
                        setSuggestedAccounts([]);
                    })
                    .catch(() => undefined);
            },
        });
    };

    const accountPuuidKey = useMemo(
        () => accounts.map((account) => account.puuid).filter(Boolean).join(","),
        [accounts],
    );
    const accountIdKey = useMemo(
        () => accounts.map((account) => account.id).filter((id) => id != null).join(","),
        [accounts],
    );

    useEffect(() => {
        if (!player || accountsLoading || accountsOwnerId !== player.id) {
            if (!player) {
                setSummaries([]);
                setAgents([]);
                setRoles([]);
                setMapBreakdown([]);
                setMapRoundRates(EMPTY_MAP_ROUND_RATES);
                setStatsLoading(false);
                setBreakdownLoading(false);
            }
            return;
        }

        const puuids = accountPuuidKey ? accountPuuidKey.split(",") : [];
        if (puuids.length === 0) {
            statsReq.current += 1;
            setSummaries([]);
            setAgents([]);
            setRoles([]);
            setMapBreakdown([]);
            setMapRoundRates(EMPTY_MAP_ROUND_RATES);
            setStatsLoading(false);
            setBreakdownLoading(false);
            return;
        }

        const additional_filters = {
            date__gte: from.toISOString(),
            date__lt: until.toISOString(),
            puuid__in: accountPuuidKey,
        };

        const req = ++statsReq.current;
        setStatsLoading(true);
        setBreakdownLoading(true);
        setFocus(null);

        ctx.reckonApi.soloqGameSummaries
            .list({
                ordering: ["-date"],
                additional_filters,
                only_values: SUMMARY_LIST_FIELDS as any,
                annotations: SUMMARY_JOIN_FIELDS as any,
            })
            .catch(() =>
                ctx.reckonApi.soloqGameSummaries.list({
                    ordering: ["-date"],
                    additional_filters,
                    annotations: SUMMARY_JOIN_FIELDS as any,
                }),
            )
            .then((result) => {
                if (req !== statsReq.current) return;
                setSummaries(result);
                setStatsLoading(false);
            })
            .catch(() => {
                if (req !== statsReq.current) return;
                setSummaries([]);
                setStatsLoading(false);
            });

        const sideMetrics = findSideWinMetrics(ctx.metricsReferential);
        const extraMetricCodes = [sideMetrics.roundWin, sideMetrics.atkWin, sideMetrics.defWin]
            .map((metric) => metric?.code)
            .filter((code): code is string => Boolean(code));

        const breakdownRequest = async (groupby: string) => {
            const base = {
                groupby: groupby as any,
                ordering: [groupby] as any,
                additional_filters,
                competitive_player: player.id,
                aggregates: [BREAKDOWN_AGGREGATES.join(",")] as any,
            };
            try {
                return await ctx.reckonApi.soloqGameSummaries.aggregate({
                    ...base,
                    ...(extraMetricCodes.length ? { metrics: extraMetricCodes.join(",") as any } : {}),
                });
            } catch {
                return ctx.reckonApi.soloqGameSummaries.aggregate(base);
            }
        };

        Promise.all([
            breakdownRequest("agent")
                .then((agentRows) => {
                    if (req !== statsReq.current) return;
                    setAgents(
                        asAggregateRows(agentRows, "agent")
                            .map((row) => fromAggregateRow(row, "agent", (value) => value, sideMetrics))
                            .filter((row): row is BreakdownAgg => Boolean(row))
                            .sort((a, b) => b.games - a.games || b.rating - a.rating),
                    );
                })
                .catch(() => {
                    if (req !== statsReq.current) return;
                    setAgents([]);
                }),
            breakdownRequest("role")
                .then((roleRows) => {
                    if (req !== statsReq.current) return;
                    setRoles(
                        asAggregateRows(roleRows, "role")
                            .map((row) => fromAggregateRow(row, "role", formatRoleName, sideMetrics))
                            .filter((row): row is BreakdownAgg => Boolean(row && row.id !== "Unknown"))
                            .sort((a, b) => b.games - a.games || b.rating - a.rating),
                    );
                })
                .catch(() => {
                    if (req !== statsReq.current) return;
                    setRoles([]);
                }),
        ]).finally(() => {
            if (req !== statsReq.current) return;
            setBreakdownLoading(false);
        });
    }, [player?.id, from, until, accountPuuidKey, accountsLoading, accountsOwnerId]);

    useEffect(() => {
        if (!player || accountsLoading || accountsOwnerId !== player.id || !accountPuuidKey) {
            return;
        }
        if (statsView !== "maps") return;

        const gameFilters = {
            date__gte: from.toISOString(),
            date__lt: until.toISOString(),
            puuid__in: accountPuuidKey,
        };
        const roundFilters = {
            date__gte: from.toISOString(),
            date__lt: until.toISOString(),
            account_id__in: accountIdKey,
        };
        const req = statsReq.current;
        const sideMetrics = findSideWinMetrics(ctx.metricsReferential);
        const atkMetricCode = sideMetrics.atkWin?.code;
        const defMetricCode = sideMetrics.defWin?.code;
        const pistolMetricCode = sideMetrics.pistolWin?.code;
        const mapRoundMetrics = ["rounds", pistolMetricCode, atkMetricCode, defMetricCode].filter((code): code is string => Boolean(code));

        const fetchRoundAgg = async (extraFilters: Record<string, any> = {}, metricCodes: string[] = ["rounds"]) => {
            if (!accountIdKey) return [];
            const request = {
                groupby: "map" as any,
                ordering: ["map"] as any,
                aggregates: ["win__avg"] as any,
                additional_filters: { ...roundFilters, ...extraFilters },
                metrics: [metricCodes.join(",")] as any,
            };
            try {
                return asAggregateRows(await ctx.reckonApi.soloQRoundSummaries.aggregate(request), "map");
            } catch {
                try {
                    const { metrics: _metrics, ...rest } = request;
                    return asAggregateRows(await ctx.reckonApi.soloQRoundSummaries.aggregate(rest), "map");
                } catch {
                    return [];
                }
            }
        };

        ctx.reckonApi.soloqGameSummaries
            .aggregate({
                groupby: "map" as any,
                ordering: ["map"] as any,
                additional_filters: gameFilters,
                competitive_player: player.id,
                aggregates: [BREAKDOWN_AGGREGATES.join(",")] as any,
            })
            .then((mapRows) => {
                if (req !== statsReq.current) return;
                setMapBreakdown(
                    asAggregateRows(mapRows, "map")
                        .map((row) => fromAggregateRow(row, "map", (value) => value, sideMetrics))
                        .filter((row): row is BreakdownAgg => Boolean(row))
                        .sort((a, b) => b.games - a.games || b.winRate - a.winRate),
                );
            })
            .catch(() => {
                if (req !== statsReq.current) return;
                setMapBreakdown([]);
            });

        (async () => {
            const mapRows = await fetchRoundAgg({}, mapRoundMetrics);
            if (req !== statsReq.current) return;
            setMapRoundRates({
                pistol: pistolMetricCode
                    ? mapRateStatsFromMetric(mapRows, pistolMetricCode, ["pistol_rounds", "pistol_round", "rounds_pistol"])
                    : new Map(),
                atk: atkMetricCode
                    ? mapRateStatsFromMetric(mapRows, atkMetricCode, ["atk_rounds", "attack_rounds", "rounds_atk"])
                    : new Map(),
                def: defMetricCode
                    ? mapRateStatsFromMetric(mapRows, defMetricCode, ["def_rounds", "defense_rounds", "rounds_def"])
                    : new Map(),
            });
        })().catch(() => {
            if (req !== statsReq.current) return;
            setMapRoundRates(EMPTY_MAP_ROUND_RATES);
        });
    }, [player?.id, from, until, accountPuuidKey, accountIdKey, accountsLoading, accountsOwnerId, statsView]);

    const accountRows = useMemo(() => buildAccountRows(accounts, summaries), [accounts, summaries]);
    const accountMaxStart = Math.max(0, accountRows.length - ACCOUNT_SLOTS);
    useEffect(() => {
        setAccountStart((prev) => Math.min(prev, accountMaxStart));
    }, [accountMaxStart]);
    const mapsById = useMemo(() => {
        const byId = new Map<string, Maps>();
        for (const map of ctx.mapsReferential) byId.set(map.id.toLowerCase(), map);
        return byId;
    }, [ctx.mapsReferential]);
    const accountNameByPuuid = useMemo(() => {
        const byPuuid = new Map<string, string>();
        for (const account of accounts) byPuuid.set(account.puuid, account.account_name);
        return byPuuid;
    }, [accounts]);
    const allMatches = useMemo(
        () => [...summaries].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()),
        [summaries],
    );
    const matchHistory = useMemo(
        () => allMatches.filter((summary) => matchesBreakdownFilter(summary, focus, ctx.agentsReferential)),
        [allMatches, focus, ctx.agentsReferential],
    );
    const matchItems = useMemo(() => {
        let lastVisibleDay: string | null = null;
        return allMatches.map((summary, index) => {
            const visible = matchesBreakdownFilter(summary, focus, ctx.agentsReferential);
            const dayKey = dayjs(summary.date).format("YYYY-MM-DD");
            const showDayDivider = visible && lastVisibleDay !== dayKey;
            if (visible) lastVisibleDay = dayKey;
            return { summary, visible, showDayDivider, index };
        });
    }, [allMatches, focus, ctx.agentsReferential]);
    const toggleFocus = (kind: BreakdownFilter["kind"], id: string) => {
        setFocus((prev) =>
            prev?.kind === kind && prev.id.toLowerCase() === id.toLowerCase() ? null : { kind, id },
        );
    };
    const selectedAccountId = focus?.kind === "account" ? focus.id : null;
    const selectedAccountRow = selectedAccountId
        ? accountRows.find((row) => accountKey(row.account).toLowerCase() === selectedAccountId.toLowerCase())
        : undefined;
    const focusChipLabel =
        focus?.kind === "account"
            ? selectedAccountRow
                ? accountDisplayName(selectedAccountRow).name
                : "Account"
            : focus
              ? capitalize(focus.id)
              : "";
    const selectedAgentId = focus?.kind === "agent" ? focus.id : null;
    const selectedRoleId =
        focus?.kind === "role"
            ? focus.id
            : focus?.kind === "agent"
              ? roleForAgent(focus.id, ctx.agentsReferential)
              : null;
    const displayedAgents = useMemo(() => {
        if (agents.some((row) => row.games > 0)) return agents;
        if (!summaries.length) return [];
        return aggregateFromSummaries(summaries, (summary) => summary.agent || "");
    }, [agents, summaries]);
    const displayedRoles = useMemo(() => {
        if (roles.some((row) => row.games > 0)) return roles;
        if (!summaries.length) return [];
        return aggregateFromSummaries(summaries, (summary) => roleFromSummary(summary, ctx.agentsReferential)).filter(
            (row) => row.id !== "Unknown",
        );
    }, [roles, summaries, ctx.agentsReferential]);
    const displayedMaps = useMemo(
        () => mergeMapRoundRates(buildMapStats(summaries, mapBreakdown), mapRoundRates.pistol, mapRoundRates.atk, mapRoundRates.def),
        [summaries, mapBreakdown, mapRoundRates],
    );
    const selectedMapId = focus?.kind === "map" ? focus.id : null;
    const sideMetrics = useMemo(() => findSideWinMetrics(ctx.metricsReferential), [ctx.metricsReferential]);
    const showAtk = Boolean(sideMetrics.atkWin) && displayedAgents.some((row) => row.atkWinRate != null);
    const showDef = Boolean(sideMetrics.defWin) && displayedAgents.some((row) => row.defWinRate != null);

    const refreshing = Boolean(
        player &&
            (accountsLoading || statsLoading || breakdownLoading) &&
            (accounts.length > 0 || summaries.length > 0),
    );
    const bootstrapping =
        Boolean(player) &&
        (accountsLoading || statsLoading) &&
        accounts.length === 0 &&
        summaries.length === 0;
    const noLinkedAccounts = Boolean(player) && !accountsLoading && accounts.length === 0;
    const teamName = player?.current_team || player?.last_team || "";

    return (
        <WindowContainer
            spacing={2}
            sx={{
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexShrink: 0 }}
            >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
                    {teamName ? <TeamIcon team_name={teamName} sx={{ width: 40, height: 40, flexShrink: 0 }} /> : null}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                            {player ? player.id : "Player profile"}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {player
                                ? `${teamLabel(teamName) ? `${teamLabel(teamName)} · ` : ""}SoloQ overview`
                                : "Select a player"}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <PlayerSelect value={player} onChange={setPlayer} referential={ctx.playersReferential} />
                    <DatePicker value={from} onChange={(v: Dayjs | null) => v && setFrom(v)} label="From" />
                    <DatePicker value={until} onChange={(v: Dayjs | null) => v && setUntil(v)} label="Until" />
                </Stack>
            </Stack>

            {!player ? (
                <NeonCard>
                    <NeonCardContent>
                        <Typography sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            Select a player to load their SoloQ dashboard.
                        </Typography>
                    </NeonCardContent>
                </NeonCard>
            ) : bootstrapping ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : (
                <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            opacity: refreshing ? 0.42 : 1,
                            filter: refreshing ? "saturate(0.75)" : "none",
                            transition: "opacity 0.28s ease, filter 0.28s ease",
                            pointerEvents: refreshing ? "none" : "auto",
                        }}
                    >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.7fr) minmax(280px, 0.85fr)" },
                            gridTemplateRows: { xs: "minmax(280px, 1.1fr) minmax(240px, 1fr)", lg: "1fr" },
                            gap: 2,
                            alignItems: "stretch",
                        }}
                    >
                        <NeonCard
                            sx={{
                                minWidth: 0,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                            }}
                        >
                            <NeonCardHeader
                                title={
                                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                                        <Box component="span">
                                            {`Match history${matchHistory.length ? ` · ${matchHistory.length}` : ""}`}
                                        </Box>
                                        {focus ? (
                                            <Chip
                                                size="small"
                                                onDelete={() => setFocus(null)}
                                                deleteIcon={<Close sx={{ fontSize: "14px !important" }} />}
                                                icon={
                                                    focus.kind === "agent" ? (
                                                        <AgentIcon agent_name={focus.id} sx={{ width: 16, height: 16 }} />
                                                    ) : focus.kind === "role" ? (
                                                        <RoleIcon
                                                            role={focus.id}
                                                            icon={
                                                                ctx.agentsReferential.find(
                                                                    (a) =>
                                                                        formatRoleName(a.role).toLowerCase() ===
                                                                        focus.id.toLowerCase(),
                                                                )?.role_icon
                                                            }
                                                            size={14}
                                                        />
                                                    ) : focus.kind === "account" ? (
                                                        <RankIcon
                                                            tier={selectedAccountRow?.rankTier}
                                                            rating={selectedAccountRow?.account.ranked_rating}
                                                            size={16}
                                                        />
                                                    ) : undefined
                                                }
                                                label={focusChipLabel}
                                                sx={{
                                                    height: 22,
                                                    fontWeight: 700,
                                                    fontSize: "0.7rem",
                                                    backgroundColor: `${neonColor}22`,
                                                    color: neonColor,
                                                    border: `1px solid ${neonColor}55`,
                                                    "& .MuiChip-icon": { ml: 0.5 },
                                                    "& .MuiChip-deleteIcon": { color: neonColor, opacity: 0.8 },
                                                }}
                                            />
                                        ) : null}
                                    </Stack>
                                }
                                sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                            />
                            <NeonCardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0,
                                    pt: 0,
                                    pb: "12px !important",
                                    flex: 1,
                                    minHeight: 0,
                                    ...scrollable,
                                }}
                            >
                                {allMatches.length === 0 ? (
                                    <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                        {noLinkedAccounts
                                            ? "No SoloQ account linked for this player."
                                            : "No SoloQ matches found for this player in this period."}
                                    </Typography>
                                ) : (
                                    <>
                                    {matchItems.map(({ summary, visible, showDayDivider, index }) => {
                                        const accountLabel =
                                            summary.ig_name || accountNameByPuuid.get(summary.puuid);

                                        return (
                                            <FilterSlot
                                                key={summary.pk || `${summary.game_id}-${summary.puuid}-${index}`}
                                                open={visible}
                                            >
                                                {showDayDivider && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            flexShrink: 0,
                                                            padding: "6px 12px",
                                                            marginTop: index === 0 ? 0 : 0.25,
                                                            marginBottom: 0.75,
                                                            borderRadius: "6px",
                                                            background: `linear-gradient(90deg, ${neonColor}12 0%, ${neonColor}06 30%, rgba(255,255,255,0.02) 100%)`,
                                                            borderLeft: `3px solid ${neonColor}`,
                                                            boxShadow: `inset 0 0 20px ${neonColor}08`,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.72rem",
                                                                fontWeight: 700,
                                                                color: "rgba(255,255,255,0.9)",
                                                                letterSpacing: "1px",
                                                            }}
                                                        >
                                                            {dayjs(summary.date).format("ddd, MMM D")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ pb: 0.75 }}>
                                                    <MatchHistoryRow
                                                        summary={summary}
                                                        mapRef={mapsById.get((summary.map || "").toLowerCase())}
                                                        accountLabel={accountLabel}
                                                        neonColor={neonColor}
                                                    />
                                                </Box>
                                            </FilterSlot>
                                        );
                                    })}
                                    <Fade in={matchHistory.length === 0} unmountOnExit>
                                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                            {focus
                                                ? `No ${focus.kind} matches for ${focusChipLabel} in this period.`
                                                : "No SoloQ matches found for this player in this period."}
                                        </Typography>
                                    </Fade>
                                    </>
                                )}
                            </NeonCardContent>
                        </NeonCard>

                        <Stack
                            spacing={2}
                            sx={{
                                minWidth: 0,
                                minHeight: 0,
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <NeonCard
                                sx={{
                                    flex: "0 1 auto",
                                    maxHeight: { xs: "none", lg: "38%" },
                                    minHeight: 0,
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title={`Accounts${accountRows.length ? ` · ${accountRows.length}` : ""}`}
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <Stack direction="row" spacing={0} sx={{ mr: -0.5 }}>
                                            <IconButton
                                                size="small"
                                                aria-label="Previous accounts"
                                                disabled={accountStart <= 0}
                                                onClick={() => setAccountStart((prev) => Math.max(0, prev - 1))}
                                                sx={{
                                                    color: "rgba(255,255,255,0.72)",
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                                }}
                                            >
                                                <KeyboardArrowLeft />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Next accounts"
                                                disabled={accountStart >= accountMaxStart}
                                                onClick={() =>
                                                    setAccountStart((prev) => Math.min(accountMaxStart, prev + 1))
                                                }
                                                sx={{
                                                    color: "rgba(255,255,255,0.72)",
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                                }}
                                            >
                                                <KeyboardArrowRight />
                                            </IconButton>
                                        </Stack>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        flex: "0 0 auto",
                                        minHeight: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    {noLinkedAccounts ? (
                                        <SuggestedAccountsPanel
                                            accounts={suggestedAccounts}
                                            loading={suggestionsLoading}
                                            neonColor={neonColor}
                                            onLink={linkSuggestedAccount}
                                        />
                                    ) : (
                                    <AccountCarousel
                                        rows={accountRows}
                                        start={accountStart}
                                        neonColor={neonColor}
                                        selectedAccountId={selectedAccountId}
                                        onSelect={(id) => toggleFocus("account", id)}
                                    />
                                    )}
                                </NeonCardContent>
                            </NeonCard>

                            <NeonCard
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title={statsView === "maps" ? "Map stats" : "Agent stats"}
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <Stack direction="row" spacing={0} sx={{ mr: -0.5 }}>
                                            <IconButton
                                                size="small"
                                                aria-label="Agent stats"
                                                disabled={statsView === "agents"}
                                                onClick={() => setStatsView("agents")}
                                                sx={{
                                                    color: "rgba(255,255,255,0.72)",
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                                }}
                                            >
                                                <KeyboardArrowLeft />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Map stats"
                                                disabled={statsView === "maps"}
                                                onClick={() => setStatsView("maps")}
                                                sx={{
                                                    color: "rgba(255,255,255,0.72)",
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                                                }}
                                            >
                                                <KeyboardArrowRight />
                                            </IconButton>
                                        </Stack>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }}
                                >
                                    {statsView === "maps" ? (
                                        displayedMaps.length === 0 ? (
                                            <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                                {noLinkedAccounts
                                                    ? "No SoloQ account linked for this player."
                                                    : "No SoloQ maps found for this player in this period."}
                                            </Typography>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                                    gridAutoRows: "minmax(148px, 1fr)",
                                                    gap: 0.75,
                                                    flex: 1,
                                                    minHeight: 0,
                                                    ...scrollable,
                                                }}
                                            >
                                                {displayedMaps.map((stats) => (
                                                    <MapStatCard
                                                        key={stats.map}
                                                        stats={stats}
                                                        neonColor={neonColor}
                                                        mapRef={mapsById.get(stats.map.toLowerCase())}
                                                        selected={Boolean(
                                                            selectedMapId &&
                                                                stats.map.toLowerCase() === selectedMapId.toLowerCase(),
                                                        )}
                                                        onSelect={() => toggleFocus("map", stats.map)}
                                                    />
                                                ))}
                                            </Box>
                                        )
                                    ) : displayedAgents.length === 0 ? (
                                        noLinkedAccounts ? (
                                            <SuggestedAccountsPanel
                                                accounts={suggestedAccounts}
                                                loading={suggestionsLoading}
                                                neonColor={neonColor}
                                                onLink={linkSuggestedAccount}
                                            />
                                        ) : (
                                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                            No SoloQ games found for this player in this period.
                                        </Typography>
                                        )
                                    ) : (
                                        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                                            <Box sx={{ flex: 1, minHeight: 0, ...scrollable }}>
                                                <AgentStatsList
                                                    rows={displayedAgents}
                                                    neonColor={neonColor}
                                                    showAtk={showAtk}
                                                    showDef={showDef}
                                                    selectedId={selectedAgentId}
                                                    onRowClick={(id) => toggleFocus("agent", id)}
                                                    isMuted={(id) =>
                                                        Boolean(
                                                            focus?.kind === "role" &&
                                                                roleForAgent(id, ctx.agentsReferential).toLowerCase() !==
                                                                    focus.id.toLowerCase(),
                                                        )
                                                    }
                                                />
                                            </Box>
                                            {displayedRoles.length > 0 ? (
                                                <Box sx={{ flexShrink: 0 }}>
                                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 1.25 }} />
                                                    <BreakdownStatsTable
                                                        rows={displayedRoles}
                                                        firstHeader="Role"
                                                        neonColor={neonColor}
                                                        showAtk={showAtk}
                                                        showDef={showDef}
                                                        selectedId={selectedRoleId}
                                                        onRowClick={(id) => toggleFocus("role", id)}
                                                        renderLabel={(row) => {
                                                            const agentForRole = ctx.agentsReferential.find(
                                                                (a) => formatRoleName(a.role).toLowerCase() === row.id.toLowerCase(),
                                                            );
                                                            return (
                                                                <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                                                                    <RoleIcon
                                                                        role={row.id}
                                                                        icon={agentForRole?.role_icon}
                                                                        size={22}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            fontSize: "0.85rem",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {capitalize(row.id)}
                                                                    </Typography>
                                                                </Stack>
                                                            );
                                                        }}
                                                    />
                                                </Box>
                                            ) : null}
                                        </Stack>
                                    )}
                                </NeonCardContent>
                            </NeonCard>
                        </Stack>
                    </Box>
                    </Box>
                    <Fade in={refreshing} unmountOnExit>
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 3,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                pointerEvents: "none",
                            }}
                        >
                            <CircularProgress sx={{ color: neonColor }} />
                        </Box>
                    </Fade>
                </Box>
            )}
        </WindowContainer>
    );
}
